import { Fragment, useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import type { PostResult } from '@lib/apiClient';
import { useEntraIdentity } from '@hooks/useEntraIdentity';
import { fmtMemberId } from '@lib/member-id';
import { MemberDetailPanel } from '@components/MemberDetailPanel';
import { PhoneActions } from '@components/PhoneActions';
import { rosterLineOf } from '@lib/call-guard';
import SmsConversation from '@components/SmsConversation';
import EmailReplyPanel, { memberEmailEntry } from '@components/EmailReplyPanel';
import {
  claimEvent,
  closeFile,
  fetchAttachmentUrl,
  fetchStreamUrl,
  fetchTranscriptText,
  getEvent,
  isConflict,
  isLocked,
  isNotFaxable,
  isLetterNotSendable,
  lockOwner,
  matchClear,
  matchOne,
  memberDetail,
  memberFaxability,
  memberSearch,
  recordActions,
  directWriteSentence,
  type DirectWriteOutcome,
  reviewEvent,
  saveNotes,
  uploadAttachment,
  type ActionInput,
  replayIdOf,
  replyActions,
  replyActionsToApproval,
  newClientRequestId,
  type ReplyActionsResp,
  withdrawPending,
  type WithdrawnStop,
  suggestMatch,
} from '@lib/care-api';
import { clientDisplayName, currentClient, useClientList, useFeatures } from '@lib/clients';
import { eventPageUrl } from '@lib/open-mode';
import { isReceiveOnly, RECEIVE_ONLY_WORDING } from '@lib/receive-only';
import { OP_CLASSES, REPLY_OPS, REPLY_OP_WORDING, type ReplyOp } from '@lib/reply-groups';
import { pressSentence } from '@components/ReplyActions';
import { CallFeeder } from '@components/CallFeeder';
import { TranscriptText } from '@components/TranscriptText';
import { QuickKeys } from '@components/QuickKeys';
import { groupKeysFor, keysNote } from '@lib/quick-keys';
import {
  STAGED_WORDING, STOP_WAITS_WORDING, approvedSentence, isInReviewRefusal, isPressOutcomeAction, isStopPress, pendingOf, pendingWho, pendingWords,
  pressOutcomeLine, replyPressHelp, withdrawnSentence,
} from '@lib/staged-presses';
import { claimHolderName } from '@lib/claim-holder';
import { APPROVAL_QUEUE_WORDING, FIND_MEMBER_WORDING, MEMBER_SEARCH_WORDING, memberSearchUnread } from '@lib/find-member';
import { FindMemberPanel } from '@components/FindMemberPanel';
import { SuggestionPill } from '@components/SuggestionPill';
import { EMAIL_WORDING, EmailMessage } from '@components/EmailMessage';
import { SUGGEST_WORDING } from '@lib/match-suggest';
import { LIFT_RETRY_WORDING, STOP_PENDING_HELP, STOP_PENDING_WORDS, liftFailedLines, liftRetryAnswer, liftRetryOffered, stopPendingOfRecord } from '@lib/stop-pending';
import { STOP_ENVELOPE_WORDING, stopEnvelopeFailed, stopEnvelopeMissing } from '@lib/stop-envelope';
import { MASTER_RETRY_WORDING, masterAnswerState, masterAnswerWords, masterSendState } from '@lib/master-retry';
import {
  ACTION_GROUPS,
  ACTION_META,
  ARCHIVE_ENTRIES_WORDING,
  DECLINE_WORDING,
  DNC_WORDING,
  FAX_NOT_FAXABLE_WORDING,
  type Faxability,
  MEMBER_MUTATING_ACTIONS,
  OPT_OUT_REASONS,
  REPLACEMENT_KIT_REASONS,
  TEST_COMPLETED_METHODS,
  fmtDuration,
  fmtMinutes,
  fmtTs,
  liveTatMinutes,
  matchedByLabel,
  statusLabel,
  suggestedActionBanner,
  tat,
  type ActionType,
  type EventAttachment,
  type EventRecord,
  type MemberCandidate,
  type MemberDetailResp,
  type PendingPress,
  PRACTICE_NOT_RESOLVED,
  type EventReferralHalt,
  ROSTER_WORDING,
  entryRosterCampaign,
  pickCampaign,
  rosterLabel,
  REPLY_CLASS_META,
  SMS_OPTIN_YES_REASON,
  effectiveShipPath,
  KIT_KIND_BY_TEMPLATE,
  SMS_YES_SHIP_WORDING,
  type SmsReplyBlock,
  type SmsYesShipPath,
} from '@lib/care-model';

/**
 * The Conversation roll-up preference, per browser/operator (William 2026-08-11). `null` means the
 * operator has never touched the header, which reads as OPEN - the default that makes the compose
 * box findable. An explicit collapse is remembered so the July 2026-07-14 concern about drawer
 * length is still served, by the operator's own choice rather than by a per-event rule. Storage
 * failures (private mode, blocked storage) degrade to the default; they never break the drawer.
 */
const SMS_OPEN_LS_KEY = 'membercare.drawer.smsOpen';
/** Slice C: the statuses on which the quick keys and the call panel are shown but not pressable (the server's terminal set). */
const TERMINAL_FOR_KEYS: readonly string[] = ['cleared', 'spam', 'closed', 'closed-no-action'];

/** Moved to lib/claim-holder.ts on 16 Oct 2026 (the queue's Claimed by column reads it too); re-exported. */
export { nameFromAddress } from '@lib/claim-holder';
function loadSmsOpenPref(): boolean | null {
  try {
    const v = localStorage.getItem(SMS_OPEN_LS_KEY);
    return v === '0' ? false : v === '1' ? true : null;
  } catch {
    return null;
  }
}
function saveSmsOpenPref(v: boolean | null): void {
  try {
    if (v === null) localStorage.removeItem(SMS_OPEN_LS_KEY);
    else localStorage.setItem(SMS_OPEN_LS_KEY, v ? '1' : '0');
  } catch { /* storage unavailable: the toggle still works for this session */ }
}

/**
 * EventDrawer - the ClariCommand detail panel shared by every queue (rev4; UI reworks William
 * 2026-07-08 + 2026-07-14). Section order: header + assignment (hard-lock UX), message/call
 * (roll-up), member match (roll-up: collapsed shows name + member #; expanded adds typeahead search
 * + reason-audited Full record), the PCP panel (roll-up; corrected-fax entry feeding the
 * pcp-fax-resend action), the SMS conversation, notes (auto-save; above attachments per William
 * 2026-07-14), attachments (as a table), the item-12 ACTION / DISPOSITION (what to DO: multi-select
 * structured actions + "Nothing / close matter"; drives the Final Review pills and rides to the
 * Portal at approval), Final review ("Send to review", which flips to an emerald "Sent to review"
 * once the event is in review), and history + the live-ticking resolution TAT (receivedTs ->
 * sentToReviewTs, William #11).
 *
 * REMOVED by William 2026-07-14: the Contact log (channel picker) and the item-6 Requests log.
 * Portal compatibility is untouched - the member-care-resolution/1.0 envelope keeps its contactLog
 * key (now just empty for new events, exactly like an event where no contact was logged), and
 * requests never rode the envelope at all. Historical contacts/requests already on records still
 * ride and still count as operator work for the Send gate.
 *
 * Roll-ups (William 2026-07-14): Message/Call, Member, PCP, and the Conversation collapse behind
 * play-button-sized disclosure arrows (.sec-arrow). Member defaults collapsed once matched (name +
 * member # stay visible); PCP defaults collapsed unless the event is fax-shaped; the do-not-solicit
 * banner is NEVER hidden by a roll-up. The Conversation is the one exception to the per-event reset:
 * it defaults OPEN and remembers the operator's own choice (William 2026-08-11, see `smsOpen`), so
 * the compose box is visible while a case is worked. The panel stays mounted while rolled up, so its
 * load still feeds the do-not-solicit signal.
 *
 * `variant="page"` renders the same content as a full-window page (the pop-out: one person at a
 * time, in full) - no scrim, no fixed positioning, the window itself is the single scrollbar.
 *
 * Every mutation goes through `mutate()`: a 409 stale_etag shows the reload banner; a 409
 * claimed_by_other (the rev4 hard lock) shows "Locked to {name}" and refreshes the claim state.
 * In `reviewMode` (Final Review page) a Review decision section adds Approve / Return to queue
 * (claim-exempt server-side).
 */

const ACTION_TYPES = Object.keys(ACTION_META) as ActionType[];

/** Play-button-sized disclosure arrow (William 2026-07-14: "a lot larger - like the play button").
 * Shared by every roll-up header; supersedes the 2026-07-11 modest-glyph arrow ruling for the
 * drawer's section headers. */
function SecArrow({ open }: { open: boolean }) {
  return (
    <span className="sec-arrow" aria-hidden="true">
      {open ? '▾' : '▸'}
    </span>
  );
}

const ATTACHMENT_KINDS: { value: EventAttachment['kind']; label: string }[] = [
  { value: 'recording', label: 'Recording' },
  { value: 'consent', label: 'Consent' },
  { value: 'image', label: 'Image' },
  { value: 'document', label: 'Document' },
];

const OPT_OUT_SCOPES = ['sms', 'phone', 'mail', 'all'] as const;
/**
 * M1-C (William's row 872-5, "Opt Out from an email entry"): with features.optOutEmail on, the Opt Out also
 * offers the Email channel, in the care record's own channel order (SMS, Phone, Email, Mail), and This
 * campaign only, which the server records as the reply press's campaign decline for the ENTRY's campaign.
 * Off, the four above exactly, so the drawer reads as it always did.
 */
const OPT_OUT_SCOPES_M1C = ['sms', 'phone', 'email', 'mail', 'all', 'campaign'] as const;
type OptOutScope = (typeof OPT_OUT_SCOPES_M1C)[number];
/** An option's words when they are not its value: only This campaign only (the channels show as they always have). */
const OPT_OUT_SCOPE_LABEL: Partial<Record<OptOutScope, string>> = { campaign: 'This campaign only' };
// What a DECLINE covers. NOT a channel, deliberately: a decline is about WHAT the member said no to,
// and a member who declines this kit is still reachable on every channel. Reusing the opt-out scopes
// here would have invited exactly the confusion the vocabulary exists to end (2026-09-14).
const DECLINE_SCOPES = ['offer', 'campaign'] as const;

const AUDIO_EXT = new Set(['mp3', 'wav', 'm4a', 'ogg', 'webm']);
const IMAGE_EXT = new Set(['jpg', 'jpeg', 'png']);

function extOf(name: string): string {
  return (name.includes('.') ? name.split('.').pop() ?? '' : '').toLowerCase();
}

function fmtBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

interface AddressFields {
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
}

const EMPTY_ADDR: AddressFields = { line1: '', line2: '', city: '', state: '', zip: '' };

/**
 * The words for a first read of the case that failed (16 Oct 2026). A 404 names the plan the read was
 * made under, because the likely cause is a window working another plan than the case's.
 */
export function loadErrorSentence(err: unknown): string {
  const status = (err as { status?: unknown } | null)?.status;
  if (status === 404) {
    return `This entry was not found under ${clientDisplayName(currentClient())}. It may belong to another plan, or it was moved to the archive.`;
  }
  const detail = err instanceof Error ? err.message : String(err);
  return `The entry could not be loaded (${detail}).`;
}

export interface EventDrawerProps {
  eventId: string;
  onClose: () => void;
  /** Fired after any change. When the drawer knows the event's fresh status (every mutation refetches
   * the event), it passes `{ eventId, status }` so the host queue can follow the row into its new
   * status tab instead of letting it silently vanish from the current view. Argument optional -
   * plain `onChanged()` callers stay valid. */
  onChanged: (updated?: { eventId: string; status: string; sentToApproval?: boolean }) => void;
  /** Final Review page: adds the Review decision section (Approve / Return, claim-exempt). */
  reviewMode?: boolean;
  /** The list row's hasAudio flag: starts the audio fetch in parallel with the event detail
   * fetch instead of serially after it (rev7 load-time work). */
  hasAudioHint?: boolean;
  /** 'drawer' (default) = the fixed right-hand overlay. 'page' = the pop-out full-window view
   * (William 2026-07-14): same content, no scrim, no fixed positioning, the window scrolls.
   * 'inline' = slice B (his img7, 24 Sep 2026: "all that is on the side (the details) is shown below"):
   * the same content in the member view UNDER the queue row, full width, no scrim, the page scrolls. */
  variant?: 'drawer' | 'page' | 'inline';
  /**
   * R. 82 / row 401: opened from the Archive's closed-entries list. READ-ONLY: every control that
   * could change the record is locked or hidden and the header says why. The record's own
   * `archived` flag (GET /api/care/event answered from archive/) locks the drawer the same way, so
   * an archived entry reached by any other path is read-only too.
   */
  archived?: boolean;
}

/** PART R: the Find Member match waiting on the case (its member and name), or null. */
function pendingMatchOf(ev: Pick<EventRecord, 'pending'>): { memberId: string; memberName: string } | null {
  const item = (ev.pending ?? []).find((p) => p.kind === 'match');
  if (!item) return null;
  const b = (item.body ?? {}) as Record<string, unknown>;
  const memberId = typeof b['memberId'] === 'string' ? b['memberId'] : '';
  return memberId ? { memberId, memberName: typeof b['memberName'] === 'string' ? b['memberName'] : '' } : null;
}

export function EventDrawer({ eventId, onClose, onChanged, reviewMode = false, hasAudioHint = false, variant = 'drawer', archived: archivedProp = false }: EventDrawerProps) {
  const identity = useEntraIdentity();
  const myEmail = (identity?.email ?? '').toLowerCase();
  // Row 391: what the deployment offers (the reply buttons are MEMBERCARE_REPLY_BUTTONS).
  const features = useFeatures();

  const [ev, setEv] = useState<EventRecord | null>(null);
  /** The first read's failure, in words (empty while loading or loaded). */
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState('');
  const [conflict, setConflict] = useState(false);
  const [flash, setFlash] = useState('');
  /**
   * THE MEMBER TWIN (API MEMBERCARE_OPTOUT_SEND_CLIENT on only): the server's one line when a both-channel block went on
   * but the member's stop was not locked on the platform. Never set with the switch off (the answer carries no line).
   */
  const [twinWarn, setTwinWarn] = useState('');

  const [notes, setNotes] = useState('');
  /** The deliberate note FOR the Portal (feedback #1): rides the envelope at approval; internal
   * `notes` never do. */
  const [portalNote, setPortalNote] = useState('');

  // Member typeahead + full record
  const [memberQ, setMemberQ] = useState('');
  const [memberCands, setMemberCands] = useState<MemberCandidate[]>([]);
  /** PART A: the Find Member wizard is open. */
  const [findOpen, setFindOpen] = useState(false);
  /** PART F: what the last search answered when it found nobody: the roster could not be read, or no member matched. */
  const [memberSearchEmpty, setMemberSearchEmpty] = useState<{ q: string; unread: boolean } | null>(null);
  /** Two-step confirm for Unmatch (feedback #3): removing a match needs no replacement member. */
  const [unmatchConfirm, setUnmatchConfirm] = useState(false);
  const [showFull, setShowFull] = useState(false);
  const [fullDetail, setFullDetail] = useState<MemberDetailResp | null>(null);
  /** Which member AND which roster `fullDetail` was read for, so a campaign change never shows a stale record. */
  const [fullDetailKey, setFullDetailKey] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  /**
   * R. 81 (William, 19 Sep 2026: "the drawer and UPDATE read every campaign"): the campaign ROSTER
   * this entry's member lookups read, the entry's own campaign (else the platform's, never the
   * member's), passed on member-search and member-detail. Null = the client's reference campaign,
   * which is what the server reads when nothing is named; the loaded client list says which that is
   * so the header can name it. The server applies the same rule for UPDATE (entryCampaign).
   */
  const clientDefs = useClientList();
  const rosterCampaign = ev ? entryRosterCampaign(ev) : null;
  const referenceCampaign = ev ? clientDefs.find((c) => c.slug === ev.client)?.referenceCampaign ?? null : null;
  const rosterName = rosterLabel(rosterCampaign, referenceCampaign);
  // Member-level do-not-contact signal, lifted from the SMS/calls panel's live load so the warning can
  // sit up by the phone actions. Cleared on event change so a prior member's flag never lingers.
  const [dnc, setDnc] = useState<{ doNotSolicit: string; suppressed: boolean; suppressionKnown: boolean; resubscribedTs: string } | null>(null);
  useEffect(() => { setDnc(null); }, [eventId]);

  // PCP panel
  const [pcpFax, setPcpFax] = useState('');
  const [faxCorrected, setFaxCorrected] = useState(false);
  /** Contract lane toggle: pcp-fax-resend data.initiateRefax (true = derivative -R<n> record staged
   * into _ReadyForFax; false = number update only). Default ON. */
  const [initiateRefax, setInitiateRefax] = useState(true);
  /** rev7 (William 2026-07-06): the fax-correction/re-fax controls are workflow tools, not member
   * facts - most events are not about the PCP's fax. They stay collapsed behind a "Fax tools"
   * reveal unless the event is already fax-shaped (fax referral, re-fax action checked, or a
   * correction in play). */
  const [faxToolsOpen, setFaxToolsOpen] = useState(false);

  // Actions checklist
  const [checked, setChecked] = useState<Set<ActionType>>(new Set());

  /* ---- The faxable guard's courtesy half (2026-09-06): when Re-fax to PCP is checked for a matched
     member and a fax would actually be initiated, ask whether this member's program can fax anything
     for them, and show the answer UNDER the checkbox before Send to review. The server re-checks at
     recording (the guard); this is the warning that saves the wasted keystrokes. ---- */
  const [faxability, setFaxability] = useState<Faxability | null>(null);
  const [notFaxableConfirmed, setNotFaxableConfirmed] = useState(false);
  useEffect(() => { setFaxability(null); setNotFaxableConfirmed(false); }, [eventId]);
  const faxGuardArmed = checked.has('pcp-fax-resend') && initiateRefax && !!ev?.memberId;
  const faxGuardMemberId = ev?.memberId ?? '';
  useEffect(() => {
    if (!faxGuardArmed || !faxGuardMemberId) return;
    let live = true;
    memberFaxability(faxGuardMemberId)
      .then((fx) => { if (live) setFaxability(fx); })
      .catch(() => {
        // The read failed: say so rather than pretend the member is faxable. The server still guards.
        if (live) setFaxability({ memberId: faxGuardMemberId, faxable: false, reason: 'unknown', confirmable: true, results: [], faxableSet: [], source: 'none' });
      });
    return () => { live = false; };
  }, [faxGuardArmed, faxGuardMemberId]);
  const faxWarning = faxGuardArmed && faxability && !faxability.faxable ? faxability : null;

  /* ---- L4 (William, 16 Oct 2026 about 10 PM: "block with a reason"): Resend result letter is refused for a
     member whose results are only failed tests. When it is ticked for a matched member the drawer asks the
     server (the same check it runs at recording; both sets come from the registry). Refused: the box is
     unticked and disabled, and the server's sentence says why under it. A failed read changes nothing:
     the server still refuses at recording. ---- */
  const [letterBlock, setLetterBlock] = useState<{ memberId: string; message: string } | null>(null);
  useEffect(() => { setLetterBlock(null); }, [eventId]);
  const letterArmed = checked.has('result-letter-resend') && !!ev?.memberId;
  const letterCampaign = ev?.campaign ?? null;
  useEffect(() => {
    if (!letterArmed || !faxGuardMemberId) return;
    let live = true;
    memberFaxability(faxGuardMemberId, letterCampaign)
      .then((fx) => {
        if (!live || !fx.letter || fx.letter.sendable) return;
        setLetterBlock({ memberId: faxGuardMemberId, message: fx.letter.message });
        setChecked((prev) => {
          const next = new Set(prev);
          next.delete('result-letter-resend');
          return next;
        });
      })
      .catch(() => undefined);
    return () => { live = false; };
  }, [letterArmed, faxGuardMemberId, letterCampaign]);
  const letterBlocked = letterBlock && letterBlock.memberId === (ev?.memberId ?? '') ? letterBlock.message : '';
  const [addr, setAddr] = useState<AddressFields>(EMPTY_ADDR);
  const [optOutScope, setOptOutScope] = useState<OptOutScope>('all');
  /** Opt-out reason (William feedback #5, 2026-07-14): required; reported back to Alignment. */
  const [optOutReason, setOptOutReason] = useState('');
  const [optOutReasonOther, setOptOutReasonOther] = useState('');
  const [dncDetail, setDncDetail] = useState('');
  // Rows 286 and 300's "Mark as ..." form in the details pane was removed by William's console
  // review (2 Oct 2026, image 11: "Remove."). Do Not Contact stays in the Action / disposition list.
  const [optInDetail, setOptInDetail] = useState('');
  const [declineScope, setDeclineScope] = useState<(typeof DECLINE_SCOPES)[number]>('offer');
  const [declineReason, setDeclineReason] = useState('');
  /** Test Completed method (feedback #4 dropdown); '' until the operator picks. */
  const [testMethod, setTestMethod] = useState('');
  const [genInquiryDetail, setGenInquiryDetail] = useState('');
  const [referredDetail, setReferredDetail] = useState('');
  const [consentOnFile, setConsentOnFile] = useState(true);
  /** Replacement-kit reason: now a dropdown VALUE from REPLACEMENT_KIT_REASONS (feedback #4). */
  const [kitReason, setKitReason] = useState('');
  const [kitReasonOther, setKitReasonOther] = useState('');
  const [letterNote, setLetterNote] = useState('');
  /** result-letter-resend data.originalRecordId (contract: the original letter's recordId when the
   * event is unrelated to a referral; optional - data {} is contract-valid). */
  const [letterOrigId, setLetterOrigId] = useState('');
  const [specialDesc, setSpecialDesc] = useState('');
  const [callbackDate, setCallbackDate] = useState('');
  const [callbackTime, setCallbackTime] = useState('');
  /** Item 12 exclusive disposition (William 2026-07-08): "Nothing / close matter" closes the event
   * (closed-no-action) and rides NO Portal action; mutually exclusive with the checked actions. */
  const [nothingClose, setNothingClose] = useState(false);
  /** Close file's optional note (his word, 2 Oct 2026 2:31 AM), on the close's own row. */
  const [closeNote, setCloseNote] = useState('');

  // Roll-ups (William 2026-07-14). null = automatic default: Member opens only while unmatched
  // (matching is the work; once matched the name + member # summary suffices), PCP opens only when
  // the event is fax-shaped. A header click pins an explicit boolean for this event; both reset to
  // automatic on event switch.
  const [memberOpen, setMemberOpen] = useState<boolean | null>(null);
  const [pcpOpen, setPcpOpen] = useState<boolean | null>(null);
  // Conversation roll-up. It was collapsed on non-SMS events from 2026-07-14 (William: "not all
  // interactions need the comms"); Marny reported on 2026-08-11 that texting was "not available"
  // while working a case, because the compose box lives inside this section and she never saw it
  // open. William's call the same day: OPEN BY DEFAULT, with a Text button beside the caller's
  // number. The July concern (drawer length) is answered by PERSISTING the operator's own collapse
  // rather than re-deciding it per event: a collapse now sticks for that operator across events and
  // sessions, so nobody is forced to re-close it, and nobody has to discover it.
  const [smsOpen, setSmsOpen] = useState<boolean | null>(loadSmsOpenPref);
  useEffect(() => { saveSmsOpenPref(smsOpen); }, [smsOpen]);
  // Bumped by the Text action: the Conversation opens and the compose box takes focus. A counter,
  // not a boolean, so a second click re-focuses (the operator's second attempt must not be a no-op).
  const [textFocusSeq, setTextFocusSeq] = useState(0);
  const jumpToCompose = useCallback(() => {
    setSmsOpen(true);
    setTextFocusSeq((n) => n + 1);
  }, []);

  // Attachments
  const [attKind, setAttKind] = useState<EventAttachment['kind']>('recording');
  const attInputRef = useRef<HTMLInputElement>(null);
  const [attPreview, setAttPreview] = useState<{ ref: string; url: string; name: string; media: 'audio' | 'image' | 'file' } | null>(null);

  // Review decision (reviewMode)
  const [reviewNote, setReviewNote] = useState('');

  const [audioUrl, setAudioUrl] = useState('');
  const [transcript, setTranscript] = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);
  // Message body collapses by default (William 2026-07-09): a disclosure triangle on the Message/Call
  // header expands the full text. Long Claire call dumps otherwise force a big scroll to reach Member
  // match / disposition below; the subject line stays visible as the always-on summary.
  const [messageOpen, setMessageOpen] = useState(false);
  const messageFirstLine = (text: string): string => {
    const line = text.split('\n').map((s) => s.trim()).find(Boolean) ?? text.trim();
    return line.length > 140 ? `${line.slice(0, 140)}...` : line;
  };

  // Live TAT tick while the event is still un-sent AND un-approved (resolution TAT is open).
  // approvedTs also freezes the tick: legacy approve-from-actioned records may lack sentToReviewTs.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (ev?.sentToReviewTs || ev?.approvedTs) return;
    const t = setInterval(() => setNowMs(Date.now()), 30_000);
    return () => clearInterval(t);
  }, [ev?.sentToReviewTs, ev?.approvedTs]);

  // Fax-tools state seeds ONCE per event (not on every reload): an unrelated mutation's reload
  // (log contact, save notes) must not clobber a typed-but-unrecorded fax correction - with the
  // rev7 collapsed tools that clobber would be invisible (rev7 panel finding).
  const faxSeededFor = useRef('');
  // The last record the server handed us, kept for the note-seed comparison below (state is one
  // render behind inside reload).
  const evRef = useRef<EventRecord | null>(null);
  const notesSeededFor = useRef('');
  // K4 (Care's 1:03 PM point 5): a reply press answered UNKNOWN by the platform or the master's door is sent
  // again on the SAME request id for this entry, so the retry replays and writes nothing twice.
  const replyRetry = useRef<{ op: string; eventId: string; id: string } | null>(null);
  const reload = useCallback(async (): Promise<EventRecord> => {
    const r = await getEvent(eventId);
    const prev = evRef.current;
    evRef.current = r;
    setEv(r);
    // The notes seed on a RE-load only replaces a box that still matches what the server last said.
    // Notes commit on their Save button, so any other mutation's reload lands with drafts sitting
    // unsaved in the two boxes; overwriting them would destroy the operator's own typing silently.
    // A dirty box therefore wins over the incoming record and Save stays the way to resolve it.
    if (notesSeededFor.current !== r.eventId) {
      notesSeededFor.current = r.eventId;
      setNotes(r.notes ?? '');
      setPortalNote(r.portalNote ?? '');
    } else {
      setNotes((cur) => (cur === (prev?.notes ?? '') ? (r.notes ?? '') : cur));
      setPortalNote((cur) => (cur === (prev?.portalNote ?? '') ? (r.portalNote ?? '') : cur));
    }
    // Once-per-event seeds (NOT on every reload): an unrelated mutation's reload (e.g. save notes)
    // must not clobber a typed-but-unsaved fax correction. Seeds when the record first lands under
    // this eventId.
    if (faxSeededFor.current !== r.eventId) {
      faxSeededFor.current = r.eventId;
      setPcpFax(r.pcpFaxCorrected || r.memberSnapshot?.pcp_fax || '');
      setFaxCorrected(!!r.pcpFaxCorrected);
      setInitiateRefax(true);
    }
    setNowMs(Date.now());
    return r;
  }, [eventId]);

  useEffect(() => {
    // SYNCHRONOUS reset on event switch, before the async reload lands (rev7 panel): the stale
    // event must not keep rendering under the new id - ev goes null (honest "Loading event...",
    // and wantAudio reads the NEW row's hasAudioHint instead of the old event), the previous
    // event's audio unmounts (a playing recording must never carry over - PHI attribution), and
    // the fax-tools state clears so a stale autoFaxCorrection can never post the previous event's
    // correction onto this one.
    setEv(null);
    setAudioUrl('');
    setPcpFax('');
    setFaxCorrected(false);
    setInitiateRefax(true);
    // A first read that fails is SAID with a way to try again, never left on "Loading event..." (16 Oct
    // 2026: a pop-out asked under the wrong plan answered 404 and the window waited forever).
    setLoadError('');
    void reload().catch((err: unknown) => setLoadError(loadErrorSentence(err)));
    setShowTranscript(false);
    setTranscript(null);
    setFlash('');
    // The member twin's line belongs to the case it came from: never shown under another case.
    setTwinWarn('');
    setConflict(false);
    setMemberQ('');
    setMemberCands([]);
    setShowFull(false);
    setFullDetail(null);
    setFullDetailKey('');
    setChecked(new Set());
    setAddr(EMPTY_ADDR);
    setLetterOrigId('');
    setNothingClose(false);
    setCloseNote('');
    setCallbackDate('');
    setCallbackTime('');
    // Per-action sub-inputs reset with the checklist so no reason/method text carries across events.
    setOptOutScope('all');
    setOptOutReason('');
    setOptOutReasonOther('');
    setDncDetail('');
    setOptInDetail('');
    setTestMethod('');
    setGenInquiryDetail('');
    setReferredDetail('');
    setKitReason('');
    setKitReasonOther('');
    setLetterNote('');
    setSpecialDesc('');
    setUnmatchConfirm(false);
    setFaxToolsOpen(false); // collapse the fax tools when the drawer switches events
    setMemberOpen(null); // roll-ups back to their automatic defaults on event switch
    setPcpOpen(null);
    // NOT smsOpen: the Conversation is the one roll-up that follows the OPERATOR rather than the
    // event (William 2026-08-11). Resetting it here would re-read the stored preference and then
    // immediately overwrite it with null, so a collapse would survive exactly one event.
    setReviewNote('');
    setAttPreview(null);
  }, [reload]);

  /**
   * PART P (William, 17 Oct 2026 12:45 AM: "when you get an email or a call or SMS without a match then have a
   * parsed data search against the db"): a case opened with no member and no suggestion yet asks the server for
   * the suggestion pass once, then shows what it stored. It writes the suggestion only, never a match.
   */
  const suggestAsked = useRef('');
  useEffect(() => {
    if (!ev || ev.eventId !== eventId || suggestAsked.current === eventId) return;
    if (ev.memberId || ev.matchSuggestion || archivedProp || ev.archived || (ev.direction && ev.direction !== 'inbound')) return;
    if (['cleared', 'closed', 'closed-no-action', 'spam'].includes(ev.status)) return;
    suggestAsked.current = eventId;
    void suggestMatch(eventId).then((r) => {
      if (r.ok && r.data.written) void reload().catch(() => undefined);
    }).catch(() => undefined);
  }, [ev, eventId, archivedProp, reload]);

  // One scrollbar (William 2026-07-14): while the overlay drawer is open, the page behind must not
  // scroll - the drawer body is the single vertical bar. The pop-out page variant scrolls the
  // window itself instead, so it never locks anything.
  useEffect(() => {
    if (variant !== 'drawer') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [variant]);

  // Member typeahead (debounced 300ms; >= 2 chars; thin-audited server-side). R. 81: searches the
  // ENTRY's campaign roster, so a manual match for an A1c reply offers A1c members, not FOBT ones.
  useEffect(() => {
    const q = memberQ.trim();
    if (q.length < 2) {
      setMemberCands([]);
      setMemberSearchEmpty(null);
      return;
    }
    const t = setTimeout(() => {
      void memberSearch(q, 8, rosterCampaign, !rosterCampaign)
        .then((r) => {
          setMemberCands(r.candidates);
          setMemberSearchEmpty(r.candidates.length ? null : { q, unread: memberSearchUnread(r) });
        })
        .catch(() => {
          setMemberCands([]);
          setMemberSearchEmpty({ q, unread: true });
        });
    }, 300);
    return () => clearTimeout(t);
  }, [memberQ, rosterCampaign]);

  // R. 81: a full record read for one roster is not the record for another. When the entry's campaign
  // changes (Marny picks a different one in the drawer), the reveal closes and re-reads on request.
  useEffect(() => {
    setShowFull(false);
    setFullDetail(null);
    setFullDetailKey('');
  }, [rosterCampaign]);

  // Audio blob URL. The stream cache in care-api OWNS these URLs (session in-memory cache, rev7:
  // replays and re-opens are instant) - never revoke here. hasAudioHint lets the fetch start from
  // the LIST ROW's flag, in parallel with the event-detail fetch, instead of serially after it.
  const wantAudio = ev ? ev.hasAudio : !!hasAudioHint;
  useEffect(() => {
    let stale = false;
    void (async () => {
      if (!wantAudio) {
        setAudioUrl('');
        return;
      }
      const got = await fetchStreamUrl('audio', eventId);
      if (got && !stale) setAudioUrl(got);
    })();
    return () => {
      stale = true;
    };
  }, [wantAudio, eventId]);

  // Unsaved-notes gate on every way OUT of the drawer (Escape / scrim / Close / Pop out). The two
  // note boxes are the only fields that live purely in this component until their Save button runs,
  // so leaving is the one exit the reload seed-guard above cannot cover.
  const unsavedRef = useRef(false);
  const confirmDiscard = useCallback(
    () => !unsavedRef.current || window.confirm('Your notes are not saved yet. Leave and discard them?'),
    [],
  );

  // Escape closes the drawer.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = () => {
    if (confirmDiscard()) onClose();
  };
  const requestClose = () => onCloseRef.current();
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  // Move focus into the modal dialog when it opens / switches event (WAI-ARIA dialog pattern).
  const drawerRef = useRef<HTMLElement>(null);
  useEffect(() => {
    drawerRef.current?.focus();
  }, [eventId]);

  /** Run one mutation: 409 stale_etag -> reload banner; 409 claimed_by_other -> lock note; then reload. */
  const mutate = useCallback(
    async (
      key: string,
      call: () => Promise<PostResult<unknown>>,
      okMsg: string | ((data: Record<string, unknown>) => string),
    ) => {
      setBusy(key);
      setFlash('');
      try {
        const r = await call();
        if (isLocked(r)) {
          setFlash(`Locked to ${lockOwner(r) || 'another operator'}. This event is claimed; take over to edit.`);
          const fresh = await reload();
          onChanged({ eventId, status: fresh.status });
          return;
        }
        if (isConflict(r)) {
          setConflict(true);
          return;
        }
        setConflict(false);
        if (!r.ok) {
          // A refusal that carries a sentence (campaign_unknown, roster_unavailable) shows the sentence.
          const d = r.data as { error?: string; message?: string };
          const err = d.message || d.error;
          setFlash(`Could not complete (HTTP ${r.status}${err ? `: ${err}` : ''}).`);
          return;
        }
        setFlash(typeof okMsg === 'function' ? okMsg(r.data as Record<string, unknown>) : okMsg);
        const fresh = await reload();
        onChanged({ eventId, status: fresh.status });
      } finally {
        setBusy('');
      }
    },
    [eventId, reload, onChanged],
  );

  const loadTranscript = useCallback(async () => {
    if (transcript !== null) {
      setShowTranscript((v) => !v);
      return;
    }
    const txt = await fetchTranscriptText(eventId);
    setTranscript(txt ?? '(transcript unavailable)');
    setShowTranscript(true);
  }, [eventId, transcript]);

  /** The reason-audited full-record reveal (session reason auto = "handling <eventId>"). */
  const toggleFullRecord = useCallback(async () => {
    if (showFull) {
      setShowFull(false);
      return;
    }
    if (!ev?.memberId) return;
    const key = `${ev.memberId}|${rosterCampaign ?? ''}`;
    if (fullDetail && fullDetail.memberId === ev.memberId && fullDetailKey === key) {
      setShowFull(true);
      return;
    }
    setDetailLoading(true);
    try {
      // R. 81: the record is read from the ENTRY's campaign roster; the response says which it read.
      const r = await memberDetail(ev.memberId, `handling ${eventId}`, undefined, rosterCampaign);
      if (r.ok && (r.data as MemberDetailResp).found) {
        setFullDetail(r.data as MemberDetailResp);
        setFullDetailKey(key);
        setShowFull(true);
      } else if ((r.data as { error?: string }).error === 'campaign_unknown') {
        setFlash(ROSTER_WORDING.campaignUnknown(rosterCampaign ?? ''));
      } else {
        setFlash(`Full record unavailable (HTTP ${r.status}).`);
      }
    } finally {
      setDetailLoading(false);
    }
  }, [showFull, fullDetail, fullDetailKey, ev?.memberId, eventId, rosterCampaign]);

  /** Upload one file as an attachment; returns the new entry's ref ('' on failure). */
  const uploadOne = useCallback(
    async (file: File, kind: EventAttachment['kind']): Promise<string> => {
      const r = await uploadAttachment(eventId, kind, file);
      if (isLocked(r)) {
        setFlash(`Locked to ${lockOwner(r) || 'another operator'}. This event is claimed; take over to edit.`);
        return '';
      }
      if (!r.ok) {
        const err = (r.data as { error?: string }).error;
        setFlash(`Upload failed for ${file.name} (HTTP ${r.status}${err ? `: ${err}` : ''}).`);
        return '';
      }
      return (r.data as { attachment?: { ref?: string } }).attachment?.ref ?? '';
    },
    [eventId],
  );

  const runUpload = useCallback(async () => {
    const files = attInputRef.current?.files;
    if (!files || !files.length) return;
    setBusy('attach');
    setFlash('');
    try {
      let okCount = 0;
      for (const f of Array.from(files)) {
        if (await uploadOne(f, attKind)) {
          okCount++;
          await reload(); // William 2026-07-08: each file appears in the table as it finishes uploading
        }
      }
      if (okCount) {
        setFlash(`${okCount} file${okCount > 1 ? 's' : ''} attached.`);
        if (attInputRef.current) attInputRef.current.value = '';
        const fresh = await reload();
        onChanged({ eventId, status: fresh.status });
      }
    } finally {
      setBusy('');
    }
  }, [eventId, attKind, uploadOne, reload, onChanged]);

  const viewAttachment = useCallback(
    async (att: { ref: string; name: string }) => {
      if (attPreview?.ref === att.ref) {
        setAttPreview(null);
        return;
      }
      const url = await fetchAttachmentUrl(eventId, att.ref);
      if (!url) {
        setFlash('Could not load the attachment.');
        return;
      }
      const ext = extOf(att.name);
      const media = AUDIO_EXT.has(ext) ? 'audio' : IMAGE_EXT.has(ext) ? 'image' : 'file';
      setAttPreview({ ref: att.ref, url, name: att.name, media });
    },
    [eventId, attPreview?.ref],
  );

  /** Overlay chrome for the default drawer; a static full-width <section> for the pop-out page
   * (dialog semantics only apply to the overlay). */
  const frame = (label: string, children: ReactNode) =>
    variant === 'page' || variant === 'inline' ? (
      <section className={variant === 'page' ? 'drawer drawer-page' : 'drawer drawer-inline'} aria-label={label} data-testid={variant === 'inline' ? 'event-inline' : undefined}>
        {children}
      </section>
    ) : (
      <>
        <div className="drawer-scrim" onClick={requestClose} />
        <aside ref={drawerRef} className="drawer" role="dialog" aria-modal="true" aria-label={label} tabIndex={-1}>
          {children}
        </aside>
      </>
    );

  if (!ev) {
    unsavedRef.current = false; // nothing to lose while the record is still landing
    return frame(
      'Event detail',
      loadError ? (
        <div style={{ padding: 20 }} role="alert" data-testid="event-load-error">
          <div className="note warn" style={{ marginBottom: 12 }}>{loadError}</div>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => {
              setLoadError('');
              void reload().catch((err: unknown) => setLoadError(loadErrorSentence(err)));
            }}
          >
            Try again
          </button>
        </div>
      ) : (
        <div style={{ padding: 20 }} className="muted small">
          Loading event...
        </div>
      ),
    );
  }

  const m = ev.memberSnapshot;
  /** Member roll-up default (William 2026-07-14): open while unmatched (matching is the work to
   * do), collapsed once matched - the name + member # summary stays visible. A header click pins
   * an explicit choice for this event. */
  const memberExpanded = memberOpen ?? !m;
  const claimedByMe = !!ev.assignedTo && ev.assignedTo.toLowerCase() === myEmail;
  const claimedByOther = !!ev.assignedTo && !claimedByMe;
  /** The rev4 hard lock: another operator's claim disables every mutation control here. A CLEARED
   * event is also locked (the resolution already shipped; the API refuses with 409 terminal_status,
   * so the UI matches). Spam / closed-no-action stay editable enough for the reopen buttons. */
  /** R. 82 / row 401: an archived entry is read-only whichever way it was reached (see the prop). */
  const archived = archivedProp || !!ev.archived;
  const locked = claimedByOther || ev.status === 'cleared' || archived;
  /** His word 3 Oct 2026: a plan with no texting number of its own is receive-only (lib/receive-only). */
  const receiveOnly = isReceiveOnly(ev.client);
  /** M1 part B: the plan's own reply mailbox, when member email is on and the API named one (lib/clients). */
  const replyMailbox = features.emailReply ? (clientDefs.find((c) => c.slug === ev.client)?.replyMailbox ?? '') : '';
  /** Awaiting review (William feedback #14, 2026-07-14): notes stay editable (an append during
   *  review still rides the envelope at approval), but staged DISPOSITIONS have no commit path
   *  once the Send button is spent - so the checklist freezes and Recall is the way back in. */
  const inReview = ev.status === 'in_review';
  /**
   * WAVE 3 ITEM 4 (William, 2 Oct 2026: "Both take effect once approved. and, all items go to approval
   * then take effect."): the presses QUEUED on this case, as the record carries them. Empty with the
   * deployment's MEMBERCARE_STAGED_PRESSES off, and then nothing below that reads it shows or blocks
   * anything, so the drawer is what it was. A queued press has NOT taken effect; it does when the case
   * is approved (the approver is the actor, the presser is kept).
   */
  const pending = pendingOf(ev);
  /** PART B: the Find Member match waiting on this case, if any. */
  const pendingFind = pending.find((p) => p.kind === 'match') ?? null;
  /** One reply press per case at a time: the member's reply gets one outcome, so a second waits for a Withdraw. */
  const queuedReply = pending.find((p) => p.kind === 'reply') ?? null;
  /** A queued Close file: the case closes at approval, so a second one is never offered. */
  const queuedClose = pending.some((p) => p.kind === 'close-file');
  /** Withdraw is the handling operator's, before the case is in review (in review the server refuses, 409 in_review). */
  const canWithdraw = !inReview && !locked && !TERMINAL_FOR_KEYS.includes(ev.status);
  /** Notes commit on their own Save button (William 2026-07-16): saving a note is a review act and
   *  must not claim the event, so it cannot ride Send to review or an on-blur write. Dirty = typed
   *  but not yet committed. */
  const notesDirty = notes !== (ev.notes ?? '');
  const portalNoteDirty = portalNote !== (ev.portalNote ?? '');
  unsavedRef.current = !locked && (notesDirty || portalNoteDirty);
  const handlerName = ev.operatorName || ev.assignedTo;
  /**
   * Who HOLDS the case (his console review, 2 Oct 2026), read from assignedTo: the operator stamp names
   * whoever touched the case last (an Unclaim stamps the person who pressed it), so it is used only
   * when it is the holder's own; otherwise the name is read off the holder's address.
   */
  const holderName = claimHolderName(ev);
  const isOutbound = ev.direction === 'outbound';

  /** Ingest-time SMS compliance flag: STOP -> warn banner offering to pre-check Opt out (scope sms);
   * HELP -> info note. Suggestion only - recording the action still goes through the checklist. */
  const saBanner = suggestedActionBanner(ev.suggestedAction);
  /**
   * The SMS platform already re-subscribed this number (api/src/lib/resubscribe.ts): a later START
   * reversed this STOP, automatically, before any operator saw either row. Recording an opt-out here
   * would emit an envelope that marks the member do-not-solicit PERMANENTLY, and no system in the
   * fleet can clear that - so the two suppressing dispositions are blocked, not merely warned about.
   * A member who wants to stop again says so in a later message, which is its own row.
   */
  const resubscribed = !!ev.resubscribedAfterStop;
  const blockedByResubscribe = (t: ActionType) => resubscribed && (t === 'opt-out' || t === 'do-not-contact');
  /** Row 286: the latest Do Not Contact recorded on this event, which is the notation in this console. */
  const markedDnc = [...(ev.actions ?? [])].reverse().find((a) => a.type === 'do-not-contact') ?? null;
  /** Enriched chip context by candidate id (matching v2); plain-id chips are the fallback. */
  const candInfo = new Map((ev.matchCandidateInfo ?? []).map((c) => [c.memberId, c] as const));
  // The API also records internal 'note' / 'status-change' entries (outside the FE ActionType
  // union); they do not count as operator work for the Send-to-review gate.
  // Wave 3 item 4: the rows an approval leaves for each queued press (press-applied, press-refused)
  // are the console's own bookkeeping too: SHOWN in the action log, never counted as operator work.
  const actionLog = (ev.actions ?? []).filter(
    (a) => (a.type as string) !== 'note' && (a.type as string) !== 'status-change',
  );
  const businessActions = actionLog.filter((a) => !isPressOutcomeAction(a));

  /* ---- actions checklist validation + build ---- */
  /** M1-C: the Opt Out scopes this deployment offers (features.optOutEmail); exactly today's four while it is off. */
  const optOutScopes: readonly OptOutScope[] = features.optOutEmail ? OPT_OUT_SCOPES_M1C : OPT_OUT_SCOPES;
  const toggleAction = (t: ActionType) => {
    if (blockedByResubscribe(t)) return;
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  };

  /** Corrected-fax-without-re-fax lane (confirmed contract: initiateRefax=false -> number update
   * only). When "Fax number corrected" is set but "Re-fax results to PCP" is NOT checked, Record
   * actions auto-includes a pcp-fax-resend action with initiateRefax:false so the corrected number
   * still reaches the Portal (the pcp block alone has no routing row). Skipped once this exact
   * correction is already stamped on the record (no duplicate auto-actions on repeat records). */
  const autoFaxCorrection =
    faxCorrected && !checked.has('pcp-fax-resend') && !!pcpFax.trim() && ev.pcpFaxCorrected !== pcpFax.trim();

  /**
   * WAVE 3 ITEM 4, the unsaved-ticks guard (the contract's screen item 5): with presses staged, a press
   * moves the case to review, where the dispositions freeze and Send to review is spent. Ticks made but
   * not yet saved would be stranded there, so while any are staged in this drawer no press is made from
   * it (the reply presses and the keys): "Send to review first, or untick them". Only while the
   * deployment stages presses; off, nothing is blocked by it (an Opt Out or Stop press still waits for
   * approval with its texting block on at once, I1 a, but it is not held by unsaved ticks).
   * Close file needs no guard: ticking it clears the ticked dispositions.
   */
  const unsavedTicks = checked.size + (autoFaxCorrection ? 1 : 0);
  const pressGuard = features.stagedPresses && unsavedTicks > 0 ? STAGED_WORDING.guard(unsavedTicks, inReview) : '';
  // L2 (16 Oct 2026): this case's Opt In is queued for approval whatever the switch says, so the same guard
  // holds for it with the switch off. Opt Out and Stop are not held by it there (they too wait for approval,
  // with the texting block on at once, I1 a; the guard is unchanged).
  const optInGuard = unsavedTicks > 0 ? STAGED_WORDING.guard(unsavedTicks, inReview) : '';

  const actionProblems: string[] = [];
  // Defense in depth behind the disabled checkboxes: whatever staged the selection, a suppressing
  // disposition may not RIDE from a row the platform has already released.
  for (const t of ACTION_TYPES) {
    if (checked.has(t) && blockedByResubscribe(t)) {
      actionProblems.push(`${ACTION_META[t].label} cannot be recorded here: this number texted START after this message.`);
    }
  }
  if (checked.has('change-of-address') && !(addr.line1.trim() && addr.city.trim() && addr.state.trim() && addr.zip.trim())) {
    actionProblems.push('Change of address needs line 1, city, state and zip.');
  }
  if (checked.has('replacement-kit') && !kitReason) actionProblems.push('Replacement kit needs a reason (pick one).');
  if (checked.has('replacement-kit') && kitReason === 'other' && !kitReasonOther.trim()) {
    actionProblems.push('Replacement kit reason "Other" needs a description.');
  }
  if (checked.has('opt-out') && !optOutReason) actionProblems.push('Opt-out of program needs a reason (pick one).');
  // Row 286 (17 Sep 2026): a DNC needs a reason, on the checklist as on the details-pane button.
  if (checked.has('do-not-contact') && !dncDetail.trim()) actionProblems.push(`Do Not Contact needs a reason. ${DNC_WORDING.markReasonRequired}`);
  // REQUIRED, unlike opt-out's, and the API enforces it too. A decline is the class that keeps a
  // member contactable, so an unexplained one is the row an admin cannot act on.
  if (checked.has('decline') && !declineReason.trim()) {
    actionProblems.push(`${DECLINE_WORDING.actionLabel} needs a reason. ${DECLINE_WORDING.reasonRequired}`);
  }
  if (checked.has('opt-out') && optOutReason === 'other' && !optOutReasonOther.trim()) {
    actionProblems.push('Opt-out reason "Other" needs a description.');
  }
  // M1-C: This campaign only acts for the ENTRY's campaign (the server's entryCampaign; rosterCampaign is the
  // screen's copy of that rule), and the server refuses an entry naming none. Said here before Send instead.
  if (checked.has('opt-out') && optOutScope === 'campaign' && !rosterCampaign) {
    actionProblems.push('This campaign only needs the entry\'s campaign: set the campaign in the drawer first.');
  }
  if (checked.has('test-completed') && !testMethod) actionProblems.push('Test Completed needs a method (pick one).');
  if (checked.has('pcp-fax-resend') && !pcpFax.trim()) actionProblems.push('Re-fax to PCP needs a fax number (PCP panel).');
  if (faxWarning && !faxWarning.confirmable) actionProblems.push(FAX_NOT_FAXABLE_WORDING.blockedLabel);
  if (faxWarning && faxWarning.confirmable && !notFaxableConfirmed) {
    actionProblems.push('Re-fax to PCP: this member has no faxable result. Confirm you have told the member, or untick it.');
  }
  if (faxCorrected && !checked.has('pcp-fax-resend') && !pcpFax.trim()) {
    actionProblems.push('Fax number corrected is checked: enter the corrected fax number in the PCP panel.');
  }
  if (checked.has('special-case') && !specialDesc.trim()) actionProblems.push('Report to plan needs a description.');
  if (checked.has('callback-scheduled') && !(callbackDate && callbackTime)) {
    actionProblems.push('Schedule a follow-up call needs a date and time.');
  }

  const buildActions = (): ActionInput[] => {
    const out: ActionInput[] = [];
    for (const t of ACTION_TYPES) {
      if (!checked.has(t)) continue;
      switch (t) {
        case 'change-of-address':
          out.push({
            type: t,
            detail: [addr.line1, addr.line2, addr.city, addr.state, addr.zip].filter(Boolean).join(', '),
            data: { line1: addr.line1.trim(), line2: addr.line2.trim(), city: addr.city.trim(), state: addr.state.trim(), zip: addr.zip.trim() },
          });
          break;
        case 'consent-update':
          out.push({
            type: t,
            detail: consentOnFile ? 'consent on file' : 'no consent on file',
            data: { consentOnFile },
          });
          break;
        case 'opt-out': {
          // reason (feedback #5): the human label rides data.reason so it reports back to Alignment
          // readably; 'other' rides the operator's own words.
          const reasonText = optOutReason === 'other'
            ? optOutReasonOther.trim()
            : OPT_OUT_REASONS.find((r) => r.value === optOutReason)?.label ?? optOutReason;
          out.push({
            type: t,
            detail: `opt-out of program (${optOutScope}): ${reasonText}`,
            data: { scope: optOutScope, reason: reasonText },
          });
          break;
        }
        case 'decline':
          out.push({
            type: t,
            detail: `${DECLINE_WORDING.actionLabel} (${declineScope}): ${declineReason.trim()}`,
            data: { scope: declineScope, reason: declineReason.trim() },
          });
          break;
        case 'do-not-contact':
          out.push({
            type: t,
            detail: `permanent - all communications: ${dncDetail.trim()}`,
            data: { reason: dncDetail.trim(), channel: 'all' },
          });
          break;
        case 'opt-in':
          out.push({ type: t, ...(optInDetail.trim() ? { detail: optInDetail.trim() } : {}) });
          break;
        case 'test-completed': {
          const methodLabel = TEST_COMPLETED_METHODS.find((x) => x.value === testMethod)?.label ?? testMethod;
          out.push({ type: t, detail: methodLabel, data: { method: testMethod } });
          break;
        }
        case 'general-inquiry':
          out.push({ type: t, ...(genInquiryDetail.trim() ? { detail: genInquiryDetail.trim() } : {}) });
          break;
        case 'referred-to-client-services':
          out.push({ type: t, ...(referredDetail.trim() ? { detail: referredDetail.trim() } : {}) });
          break;
        case 'pcp-fax-resend':
          out.push({
            type: t,
            detail: `re-fax results to PCP at ${pcpFax.trim()}${faxCorrected ? ' (corrected)' : ''}${initiateRefax ? '' : ' - number update only, no re-fax'}`,
            data: {
              pcpFax: pcpFax.trim(), faxCorrected, initiateRefax, ...haltKey(ev),
              ...(faxWarning && notFaxableConfirmed ? { notFaxableConfirmed: true } : {}),
            },
          });
          break;
        case 'replacement-kit': {
          // Structured reason (feedback #4 dropdown); the human label rides data.reason (the Portal
          // requires it and the plan report reads it), 'Other' rides the operator's own words.
          const kitText = kitReason === 'other'
            ? kitReasonOther.trim()
            : REPLACEMENT_KIT_REASONS.find((r) => r.value === kitReason)?.label ?? kitReason;
          out.push({ type: t, detail: kitText, data: { reason: kitText } });
          break;
        }
        case 'result-letter-resend':
          out.push({
            type: t,
            ...(letterNote.trim() ? { detail: letterNote.trim() } : {}),
            data: letterOrigId.trim() ? { originalRecordId: letterOrigId.trim() } : {},
          });
          break;
        case 'special-case':
          out.push({ type: t, detail: specialDesc.trim() });
          break;
        case 'callback-scheduled': {
          const when = [callbackDate, callbackTime].filter(Boolean).join(' ');
          out.push({ type: t, detail: when, data: { date: callbackDate, time: callbackTime } });
          break;
        }
      }
    }
    // The number-update-only lane: the corrected fax must reach the Portal even when no re-fax was
    // requested (visible to the operator via the PCP-panel note; contract initiateRefax=false).
    if (autoFaxCorrection) {
      out.push({
        type: 'pcp-fax-resend',
        detail: `corrected PCP fax number ${pcpFax.trim()} (number update only, no re-fax)`,
        data: { pcpFax: pcpFax.trim(), faxCorrected: true, initiateRefax: false, ...haltKey(ev) },
      });
    }
    return out;
  };

  /* ---- Item 12: "Nothing / close matter" disposition (closes the event, no Portal action). ---- */
  // Persist the current notes ATOMICALLY with the close (one write): a separate notes save after this
  // one would 409 against the now-terminal record and drop the note. Carrying `status` also keeps the
  // claim on this write - closing a matter is handling it, not reviewing it.
  /** Close file (William, 2 Oct 2026 2:31 AM): logs the close on the case and closes it; nothing is
   * sent to the member. The running notes are saved with it, exactly as the old close saved them.
   * WAVE 3 ITEM 4: when the answer says `staged`, the close was QUEUED (the case moved to review and
   * closes when it is approved), so the sentence says queued, never closed, and the tick comes off:
   * the queued close is listed under Waiting for approval, and a second one is never offered. */
  const runCloseMatter = () =>
    void mutate('close', async () => {
      const r = await closeFile(eventId, { note: closeNote, notes });
      if (r.ok && (r.data as { staged?: unknown }).staged === true) {
        setNothingClose(false);
        setCloseNote('');
      }
      return r;
    }, (data) => (data.staged === true
      ? STAGED_WORDING.closeFileQueued
      : 'File closed: answered, no further follow-up. Nothing was sent to the member.'));

  /* ---- WAVE 3 ITEM 4: Withdraw one queued press before the case is approved. Its own handler, not
     mutate(): the server's refusal while the case is in review is a 409 too (in_review), and that one
     is a sentence to show, never the "this event changed" reload banner. ---- */
  const runWithdraw = async (p: PendingPress) => {
    if (busy) return;
    setBusy(`withdraw-${p.id}`);
    setFlash('');
    try {
      const r = await withdrawPending(eventId, p.id);
      if (isLocked(r)) {
        setFlash(`Locked to ${lockOwner(r) || 'another operator'}. This event is claimed; take over to edit.`);
        const f = await reload();
        onChanged({ eventId, status: f.status });
        return;
      }
      if (isInReviewRefusal(r)) {
        setFlash(STAGED_WORDING.inReviewRefused);
        const f = await reload();
        onChanged({ eventId, status: f.status });
        return;
      }
      if (isConflict(r)) {
        setConflict(true);
        return;
      }
      setConflict(false);
      if (!r.ok) {
        const d = r.data as { error?: string; message?: string };
        const err = d.message || d.error;
        setFlash(`Could not withdraw (HTTP ${r.status}${err ? `: ${err}` : ''}). The press is still queued.`);
        await reload();
        return;
      }
      // I2 a (William, 17 Oct 2026): a withdrawn stop's texting block is lifted, or kept and why, or NOT lifted and why.
      setFlash(withdrawnSentence(pendingWords(p), (r.data as { stops?: WithdrawnStop[] }).stops));
      const fresh = await reload();
      onChanged({ eventId, status: fresh.status });
    } finally {
      setBusy('');
    }
  };

  /* ---- Send to review gating (William #3/#11 + Portal contract 4b.4) ---- */
  const sendEligible = ev.status === 'new' || ev.status === 'in_progress' || ev.status === 'actioned';
  // Staged (not-yet-committed) selections: the per-section save buttons were removed (William
  // 2026-07-09), so the gate reads what is STAGED in the drawer, committed together at Send to review.
  // Contacts/requests already ON the record (logged before their sections were removed, William
  // 2026-07-14) still count as operator work, so those events stay sendable.
  const stagedActions = buildActions();
  const hasWork =
    stagedActions.length > 0 ||
    !!notes.trim() ||
    !!portalNote.trim() ||
    businessActions.length > 0 ||
    (ev.contactLog?.length ?? 0) > 0 ||
    (ev.requests?.length ?? 0) > 0 ||
    !!(ev.notes && ev.notes.trim()) ||
    !!(ev.portalNote && ev.portalNote.trim()) ||
    // PART L: a Find Member match waiting, or files on the case, are work to send.
    !!pendingFind ||
    (ev.attachments?.length ?? 0) > 0;
  const unmatchedMutating =
    !ev.memberId &&
    // PART B: the match waiting is applied first at approval, so the dispositions have their member.
    !pendingFind &&
    (stagedActions.some((a) => MEMBER_MUTATING_ACTIONS.has(a.type)) ||
      businessActions.some((a) => MEMBER_MUTATING_ACTIONS.has(a.type)));
  const sendBlockedTitle = !sendEligible
    ? 'Only new / in progress / actioned events can be sent to review.'
    : unmatchedMutating
      ? 'Match a member first: the dispositions change member data.'
      : actionProblems.length > 0
        ? actionProblems.join(' ')
        : !hasWork
          ? 'Pick a disposition or add notes first.'
          : '';

  /* ---- Send to review: commit every staged section (dispositions, notes) in ONE action, then
     send. Removing the per-section save buttons (William 2026-07-09) means selections are locked in
     HERE. Ordered + abort-on-failure: a failed commit never sends a half-saved event. recordActions
     is an idempotent replace; notes are re-saved only when changed (their Save buttons may already
     have persisted them). The requests/contact-log steps left with their sections (William 2026-07-14).
     The notes steps are notes-only bodies, so the claim is taken by the send itself, not by them. ---- */
  const runSendToReview = async () => {
    if (busy) return;
    if (actionProblems.length > 0) {
      setFlash(actionProblems.join(' '));
      return;
    }
    setBusy('review');
    setFlash('');
    setTwinWarn('');
    try {
      const steps: { label: string; call: () => Promise<PostResult<unknown>> }[] = [];
      if (checked.size > 0 || autoFaxCorrection) {
        steps.push({ label: 'the dispositions', call: () => recordActions(eventId, buildActions()) });
      }
      if (notes !== (ev.notes ?? '')) steps.push({ label: 'notes', call: () => saveNotes(eventId, { notes }) });
      if (portalNote !== (ev.portalNote ?? '')) {
        steps.push({ label: 'the Portal note', call: () => saveNotes(eventId, { portalNote }) });
      }

      let directNote = '';
      let queuedNote = '';
      let stopNote = '';
      let twinNote = '';
      for (const step of steps) {
        const r = await step.call();
        // The member twin: shown at once, so it stays on screen whatever the later steps do (the block is already on).
        const tw = (r.data as { twinNotice?: unknown } | null)?.twinNotice;
        if (typeof tw === 'string' && tw) {
          twinNote = tw;
          setTwinWarn(tw);
        }
        // Row 216: a drawer opt-out or Do Not Contact reports what reached the opt-out record.
        const dw = (r.data as { directWrite?: DirectWriteOutcome[] }).directWrite;
        if (dw?.length) directNote = dw.map(directWriteSentence).filter(Boolean).join(' ');
        // L2: an opt back in is queued for approval, never written at once; the server's sentence says so.
        const st = (r.data as { staged?: { message?: string }[] }).staged;
        if (st?.length) queuedNote = st.map((x) => x.message ?? '').filter(Boolean).join(' ');
        // I1 a (William, 17 Oct 2026): an Opt-out of program or Do Not Contact waits for approval with its texting
        // block on at once; the server's own sentence says so.
        const sp = r.data as { stopPending?: boolean; message?: string };
        if (sp?.stopPending === true && sp.message) stopNote = sp.message;
        if (isLocked(r)) {
          setFlash(`Locked to ${lockOwner(r) || 'another operator'}. This event is claimed; take over to edit.`);
          const f = await reload();
          onChanged({ eventId, status: f.status });
          return;
        }
        if (isLetterNotSendable(r)) {
          // L4: the server refused the result letter (only failed tests). Its sentence, the box unticked and held.
          const msg = String((r.data as { message?: string }).message ?? 'this member has only failed tests');
          if (ev.memberId) setLetterBlock({ memberId: ev.memberId, message: msg });
          setChecked((prev) => {
            const next = new Set(prev);
            next.delete('result-letter-resend');
            return next;
          });
          setFlash(`Not recorded: ${msg} Nothing was sent to review.`);
          return;
        }
        if (isNotFaxable(r)) {
          // The guard refused the re-fax (the courtesy read may have been stale or skipped). Show the
          // server's own sentence and re-arm the warning so the operator can confirm or untick.
          const msg = String((r.data as { message?: string }).message ?? 'this member has no faxable result');
          setFlash(`Not recorded: ${msg} Nothing was sent to review.`);
          if (ev.memberId) {
            memberFaxability(ev.memberId).then(setFaxability).catch(() => undefined);
          }
          return;
        }
        if (isConflict(r)) {
          setConflict(true);
          return;
        }
        if (!r.ok) {
          const err = (r.data as { error?: string }).error;
          setFlash(`Could not save ${step.label} (HTTP ${r.status}${err ? `: ${err}` : ''}). Nothing was sent to review.`);
          await reload();
          return;
        }
      }
      setConflict(false);

      const rv = await reviewEvent(eventId, 'send');
      if (isLocked(rv)) {
        setFlash(`Locked to ${lockOwner(rv) || 'another operator'}. This event is claimed; take over to edit.`);
        const f = await reload();
        onChanged({ eventId, status: f.status });
        return;
      }
      if (isConflict(rv)) {
        setConflict(true);
        return;
      }
      if (!rv.ok) {
        const err = (rv.data as { error?: string }).error;
        const why = String((rv.data as { message?: string }).message ?? err ?? '');
        setFlash(`Saved your work, but ${APPROVAL_QUEUE_WORDING.failed(rv.status, why)} Try Send again.`);
        await reload();
        return;
      }

      // Sent: clear the staged action UI (reload re-seeds notes from the record).
      setChecked(new Set());
      setAddr(EMPTY_ADDR);
      setKitReason('');
      setKitReasonOther('');
      setOptOutReason('');
      setOptOutReasonOther('');
      setDncDetail('');
      setOptInDetail('');
      setTestMethod('');
      setGenInquiryDetail('');
      setReferredDetail('');
      setLetterNote('');
      setLetterOrigId('');
      setSpecialDesc('');
      setCallbackDate('');
      setCallbackTime('');
      setFlash(['Saved and sent to the Approval Queue.', queuedNote, stopNote, directNote].filter(Boolean).join(' '));
      // PART L (William, 16 Oct 2026 about 11:24 PM: "at that time the window closes and the row disappears"):
      // the host removes the row and the case window closes.
      onChanged({ eventId, status: 'in_review', sentToApproval: true });
      // The member twin's line must be read, so the window stays open while it shows; the operator closes it.
      // The case is re-read first, so it shows as sent (in review) and Send cannot be pressed twice.
      if (twinNote) {
        await reload();
        return;
      }
      onClose();
      return;
    } finally {
      setBusy('');
    }
  };

  /* ---- Recall from review (William feedback #14, 2026-07-14): the handling operator pulls their
     own item back out of the review queue (in_review -> in_progress) to change dispositions or add
     more work. Claim-locked server-side; approval remains the point of no return. ---- */
  const runRecall = async () => {
    if (busy) return;
    setBusy('review');
    setFlash('');
    try {
      const rv = await reviewEvent(eventId, 'recall');
      if (isLocked(rv)) {
        setFlash(`Locked to ${lockOwner(rv) || 'another operator'}. This event is claimed; take over to edit.`);
        const f = await reload();
        onChanged({ eventId, status: f.status });
        return;
      }
      if (!rv.ok) {
        const err = (rv.data as { error?: string }).error;
        setFlash(`Could not recall (HTTP ${rv.status}${err ? `: ${err}` : ''}).`);
        await reload();
        return;
      }
      setFlash('Recalled from review - back in progress. Dispositions and notes are editable again.');
      const fresh = await reload();
      onChanged({ eventId, status: fresh.status });
    } finally {
      setBusy('');
    }
  };

  /* ---- live resolution TAT (frozen at sentToReviewTs, or approvedTs for approve-from-actioned) ---- */
  const tatFrozenAt = ev.sentToReviewTs || ev.approvedTs;
  const tatMins = liveTatMinutes(ev.receivedTs, tatFrozenAt, nowMs);
  const tatLabel = tatMins === null ? '-' : `${fmtMinutes(tatMins)}${tatFrozenAt ? '' : ' (open)'}`;

  /** The top row's client and campaign (William's console review, 2 Oct 2026, image 10: "Do not
   * include this - just put on the top row area: which client, campain"). The campaign is the
   * entry's own, else the one the text it answered names (slice B), else said as none. */

  const section: CSSProperties = { padding: '14px 18px', borderTop: '1px solid var(--rule)' };
  /** The commit row under a note box: state on the left of its own Save, both hard right. */
  const saveRow: CSSProperties = {
    display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 6,
  };

  /** Pop out (William 2026-07-14): the event in its own browser window, one person in full. The
   * same-origin window.open COPIES sessionStorage into the child, so the MSAL cache rides along and
   * the pop-out authenticates silently - never pass noopener here (it suppresses that copy). The
   * in-app drawer closes so the event is being edited in one place. */
  const popOut = () => {
    if (!confirmDiscard()) return; // the drafts stay in this component; the new window reads the record
    window.open(eventPageUrl(ev.eventId), `mc-event-${ev.eventId}`, 'popup=yes,width=1080,height=1000');
    onClose();
  };

  return frame(
    `Event ${ev.eventId}`,
    <>
        {/* Header */}
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--ink-900)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {/* His word, 16 Oct 2026 8:47 PM, of the pill strip that stood here (source, Inbound, status,
                Receive-only, plan and campaign): "Remove this". The facts stay where they work: the queue
                rows show them, the receive-only rule still gates every press and its notes below, and an
                archived case keeps its read-only note. Only the strip went. */}
            {variant !== 'page' && !archived && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ marginLeft: 'auto' }}
                title="Open this event in its own window (one person at a time, in full)"
                onClick={popOut}
              >
                Pop out
              </button>
            )}
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={variant !== 'page' && !archived ? undefined : { marginLeft: 'auto' }}
              onClick={requestClose}
            >
              {variant === 'page' ? 'Close window' : 'Close'}
            </button>
          </div>
          {/* William's console review (2 Oct 2026, image 9): "This goes as the second row item - claim.
              Also, add a transfer button and then a drop down - to Alex, Sherry. Also, add a unclaim
              button - to put back in the main queue." Claim, Transfer and Unclaim change only who holds
              the case; the server writes one access-log row (care-claim) for each. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8 }} data-testid="assignment-row">
            {archived ? (
              <span className="micro muted">
                {holderName || handlerName ? `Handled by ${holderName || handlerName}.` : 'Nobody was named as the handler.'} Archived: the assignment cannot change.
              </span>
            ) : (
              <>
                {/* William, 16 Oct 2026 8:52 PM: "Just make this claim and unclaim." One button: Claim while I
                    do not hold the case, Unclaim while I do. Transfer is off the screen (its server route is
                    untouched). A case someone else holds names them and offers Claim, the audited takeover
                    it always offered; releasing their claim from here is no longer offered (the server still
                    allows it, audited, and that rule is unchanged). */}
                {claimedByMe ? (
                  <span className="pill pill-primary" data-testid="claim-state">Claimed by you</span>
                ) : ev.assignedTo ? (
                  <span className="pill pill-warning" data-testid="claim-state">Claimed by {holderName}</span>
                ) : (
                  <span className="pill" data-testid="claim-state">Unclaimed</span>
                )}
                {claimedByMe ? (
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={busy === 'claim'}
                    title="Put this case back in the main queue, held by nobody"
                    onClick={() => void mutate('claim', () => claimEvent(eventId, true), 'Unclaimed. The case is back in the main queue, held by nobody.')}
                  >
                    Unclaim
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-teal btn-sm"
                    disabled={busy === 'claim'}
                    title={ev.assignedTo ? `Take this case from ${holderName} (the access log records it)` : 'Take this case: it stays in the main queue under your name'}
                    onClick={() => void mutate(
                      'claim',
                      () => claimEvent(eventId, false),
                      ev.assignedTo ? `Claimed from ${holderName}. You are handling this case.` : 'Claimed. You are handling this case; it stays in the main queue under your name.',
                    )}
                  >
                    Claim
                  </button>
                )}
                {/* PART A (William, 16 Oct 2026 about 10:45 PM: "make a button on the top that says "Find Member""). */}
                {!TERMINAL_FOR_KEYS.includes(ev.status) && ev.status !== 'in_review' && (
                  <button
                    type="button"
                    className="btn btn-sm"
                    data-testid="find-member-button"
                    disabled={claimedByOther}
                    title={claimedByOther ? `Claimed by ${holderName}` : 'Search the plan\'s member list and match this case to one member, through the Approval Queue'}
                    onClick={() => setFindOpen((o) => !o)}
                  >
                    {FIND_MEMBER_WORDING.button}
                  </button>
                )}
              </>
            )}
          </div>
          {findOpen && !archived && (
            <FindMemberPanel
              ev={{
                eventId: ev.eventId,
                ...(ev.content ? { content: ev.content } : {}),
                ...(ev.callerName ? { callerName: ev.callerName } : {}),
                ...(ev.callerDob ? { callerDob: ev.callerDob } : {}),
                ...(ev.fromPhone ? { fromPhone: ev.fromPhone } : {}),
                ...(ev.suggestedAction ? { suggestedAction: ev.suggestedAction } : {}),
                ...(ev.claireRequestType ? { claireRequestType: ev.claireRequestType } : {}),
                ...(ev.requests ? { requests: ev.requests } : {}),
                ...(ev.actions ? { actions: ev.actions } : {}),
              }}
              transcript={transcript}
              rosterCampaign={rosterCampaign}
              rosterLabel={rosterName}
              planCampaigns={Object.keys(clientDefs.find((c) => c.slug === ev.client)?.programScopes ?? {})}
              planName={clientDefs.find((c) => c.slug === ev.client)?.shortName || clientDefs.find((c) => c.slug === ev.client)?.displayName || ev.client}
              campaignOffered={features.optOutEmail}
              sendOnMatch={features.optOutEverywhere === true}
              {...(ev.matchSuggestion?.memberId ? { suggestion: ev.matchSuggestion } : {})}
              disabled={claimedByOther}
              onClose={() => setFindOpen(false)}
              onQueued={(message) => {
                setFindOpen(false);
                setFlash(message);
                void reload().then((fresh) => onChanged({ eventId, status: fresh.status }));
              }}
            />
          )}
          {/* PART R (William, 17 Oct 2026): the suggestion at the case top, with its percent and reasons, and
              "Not this member" to clear it. Once a match waits for approval the case says so below instead
              (match-pending), so the suggestion is not shown twice. */}
          {!ev.memberId && !pendingMatchOf(ev) && ev.matchSuggestion?.memberId && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8 }} data-testid="case-suggestion">
              <SuggestionPill suggestion={ev.matchSuggestion} testId="case-suggest-pill" />
              {!archived && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  data-testid="not-this-member"
                  disabled={claimedByOther || busy === 'suggest'}
                  title={claimedByOther ? `Claimed by ${holderName}` : SUGGEST_WORDING.notThisHelp}
                  onClick={() => void mutate('suggest', () => suggestMatch(eventId, ev.matchSuggestion?.memberId ?? ''), SUGGEST_WORDING.cleared)}
                >
                  {SUGGEST_WORDING.notThis}
                </button>
              )}
            </div>
          )}
          {/* PART N (William, 17 Oct 2026 1:05 AM): the stop's blocks are on while it waits for approval. */}
          {stopPendingOfRecord(ev) && (
            <div className="note warn" role="note" data-testid="stop-pending-case" style={{ marginTop: 6 }}>
              <b>{STOP_PENDING_WORDS}.</b> {STOP_PENDING_HELP}
            </div>
          )}
          {/* T-1025-CARE-08: a lift that did not happen leaves its block open; the case says why, in Care's words. */}
          {liftFailedLines({ ...ev, archived }).map((l) => (
            <div key={l.id} className="note warn" role="note" data-testid="stop-lift-failed" style={{ marginTop: 6 }}>
              {l.words}
            </div>
          ))}
          {claimedByOther && !archived && (
            <div className="micro muted" style={{ marginTop: 4 }}>
              Claimed by {holderName}: the edit controls below stay locked until you claim it.
            </div>
          )}
          <div className="mono small muted" style={{ marginTop: 6 }}>
            {ev.eventId}
          </div>
          <div className="small" style={{ marginTop: 2 }}>
            {isOutbound ? 'Worklist created' : 'Received'} {fmtTs(ev.receivedTs)}
            {ev.fromPhone ? (
              <>
                {' '}
                ·
                {' from '}
                {/* Text jumps to the compose box below. The number shown here IS the one the
                    composer sends to (the API prefers an explicit phone over the roster number), so
                    the button cannot promise a destination it will not use. Both outbound actions
                    are gated on the live do-not-solicit / opt-out signal the conversation panel
                    reports, the same signal the banner in the Member section renders. The Call asks
                    the stop check bound to this case and its from-phone (the office, 17 Oct 2026,
                    interim line 5: every member call passes the stop check). */}
                <PhoneActions
                  phone={ev.fromPhone}
                  compact
                  blocked={receiveOnly || (!!dnc && (dnc.doNotSolicit !== '' || dnc.suppressed))}
                  checkBeforeCall={{ eventId: ev.eventId, line: 'from' }}
                  {...(archived ? {} : { onText: jumpToCompose })}
                />
              </>
            ) : ev.fromAddress ? ` · from ${ev.fromAddress}` : ''}
            {` · TAT ${tatLabel}`}
            {/* Internal-numbers roster (feedback #17): badge when the sender is CDX staff/test. The
                one-click add moved down to the foot of the Action / disposition section (William's
                console review, 2 Oct 2026, image 7: "Remove 'mark internal' (will put it down lower"). */}
            {ev.internal ? (
              <span
                className="pill pill-warning"
                style={{ marginLeft: 6 }}
                title="On the internal-numbers roster (CDX team / test device). Never matched to a member; matching never learns this number."
              >
                CDX internal
              </span>
            ) : null}
          </div>
          {ev.referral && (
            <div className="micro muted" style={{ marginTop: 2 }}>
              referral {ev.referral.reason} · record {ev.referral.recordId}
              {ev.referral.referredAt ? ` · ${fmtTs(ev.referral.referredAt)}` : ''}
              {ev.referral.resolvedByPortal ? (
                <span className="pill pill-success" style={{ marginLeft: 6 }} title="The Portal closed this referral upstream (its file moved to _Resolved)">
                  resolved by the Portal · {fmtTs(ev.referral.resolvedByPortal)}{ev.referral.resolvedBy ? ` · ${ev.referral.resolvedBy}` : ''}
                </span>
              ) : null}
            </div>
          )}
          {ev.referral?.halt && <HaltPhysicians halt={ev.referral.halt} />}
          {ev.smsReply && (
            <SmsReplyPanel
              reply={ev.smsReply}
              ship={replyShipTo(ev)}
              shipPath={effectiveShipPath(ev.smsYesShipPath ?? 'undecided', ev.smsReply)}
              disabled={locked || busy === 'sms-yes'}
              // Row 391: the reply buttons (MEMBERCARE_REPLY_BUTTONS) act on THIS entry alone, through
              // the same route as the queue's group presses. Refused rows come back named, not thrown.
              replyButtons={features.replyButtons}
              // Wave 3 item 4: no press while ticks are unsaved (the guard), nor while a reply press is
              // already queued on this case; the reason is said under the buttons. Both are '' with the
              // deployment's staged presses off, so the buttons are what they were.
              pressDisabled={locked || busy.startsWith('reply-') || !ev.memberId || ['cleared', 'spam', 'closed', 'closed-no-action'].includes(ev.status) || !!pressGuard || !!queuedReply}
              pressNote={pressGuard || (queuedReply ? STAGED_WORDING.replyQueued(pendingWords(queuedReply)) : '')}
              onPress={(op) => {
                const guard = pressGuard || (op === 'opt-in' ? optInGuard : '');
                if (guard) { setFlash(guard); return; }
                const label = (ev.smsReply?.classification === 'YES_KIT' || ev.smsReply?.classification === 'DECLINE_CAMPAIGN')
                  ? REPLY_OP_WORDING[op].button : REPLY_OP_WORDING[op].move;
                // L2 (William, 16 Oct 2026 about 10 PM: "opt-in waits for review"): this case's Opt In is
                // QUEUED for Final Review (toApproval), like the queue's Opt-In button, whatever the
                // staging switch says. I1 a (William, 17 Oct 2026): Opt Out and Stop wait too, whatever the
                // switch says: the server stages them (api/src/lib/stop-approval.ts) and puts the texting block
                // on at once, PROVISIONAL. Their confirm box says so (replyPressHelp), never "not now".
                const toApproval = op === 'opt-in';
                const confirmTail = isStopPress(op) ? '' : (features.stagedPresses || toApproval ? `

${STAGED_WORDING.confirmQueued}` : '');
                if (!window.confirm(`${label}?

${replyPressHelp(op)}${confirmTail}`)) return;
                const held = replyRetry.current;
                const replay = held && held.op === op && held.eventId === eventId ? held.id : undefined;
                void mutate(`reply-${op}`, async () => {
                  const r = toApproval
                    ? await replyActionsToApproval('opt-in', [eventId], replay ?? newClientRequestId())
                    : await replyActions(op, [eventId], replay);
                  if (r.ok) {
                    const again = replayIdOf(r.data as ReplyActionsResp);
                    replyRetry.current = again ? { op, eventId, id: again } : null;
                  }
                  return r;
                }, (data) => pressSentence(label, [data as unknown as ReplyActionsResp]));
              }}
              onConfirm={(path) => void mutate(
                'sms-yes',
                // 'care': kit-request (addendum /1.1) with the address Care confirmed. NOT replacement-kit,
                // which is the 26 FOBT second-kit lane. Internal until S5 accepts /1.1, and HELD there until
                // an A1c-KED rail exists, so nothing ships from this click tonight.
                // 'portal': general-inquiry is envelope-FILTERED (internal only), so it can never ship.
                () => recordActions(eventId, path === 'care'
                  ? [{
                    type: 'kit-request',
                    detail: 'texted yes: address confirmed by Care, kit requested',
                    data: {
                      campaign: ev.campaign ?? ev.smsReply?.campaignName ?? '',
                      kitKind: KIT_KIND_BY_TEMPLATE[ev.smsReply?.templateKey ?? ''] ?? '',
                      source: SMS_OPTIN_YES_REASON,
                      messageId: ev.smsReply?.messageId ?? '',
                      templateKey: ev.smsReply?.templateKey ?? '',
                      requestedAt: ev.receivedTs,
                      shipTo: replyShipTo(ev)
                        ? { ...replyShipTo(ev)!, zip4: '', confirmedBy: myEmail, confirmedAt: new Date().toISOString() }
                        : null,
                    },
                  }]
                  : [{ type: 'general-inquiry', detail: path === 'auto' ? 'texted yes: address confirmed by Care (requested automatically; the Portal ships this kit)' : 'texted yes: address confirmed by Care (the Portal ships this kit)' }]),
                path === 'care' ? 'Kit recorded to ship at review.' : 'Address confirmation recorded.',
              )}
            />
          )}
        </div>

        {/* Body. Drawer variant: the ONE scrolling region (the page behind is scroll-locked and
            every inner panel flows - William 2026-07-14 single-scrollbar ruling). Page variant:
            no inner scroll at all; the window scrolls. */}
        <div style={variant === 'drawer' ? { overflowY: 'auto', flex: '1 1 auto' } : undefined}>
          {archived && (
            <div style={{ padding: '12px 18px 0' }}>
              <div className="note" role="status" data-testid="archived-readonly-note">
                {ARCHIVE_ENTRIES_WORDING.readOnly}
              </div>
            </div>
          )}
          {/* Slice C (his Care console instructions, 24 Sep 2026): the call panel on a calling-list entry,
              never on an archived entry. Its three rows of quick keys (Status, Action, Explain) were
              removed by his console review (2 Oct 2026, image 8: "Remove this (will handle below)"):
              the Action / disposition list below carries those functions. */}
          {!archived && ev.source === 'out-calling' && (
            <CallFeeder ev={ev} locked={locked || TERMINAL_FOR_KEYS.includes(ev.status)} blocked={receiveOnly || (!!dnc && (dnc.doNotSolicit !== '' || dnc.suppressed))} />
          )}
          {conflict && (
            <div style={{ padding: '12px 18px' }}>
              <div className="note warn">
                <b>This event changed.</b> Another operator updated it while you were working. Reload to
                see the latest, then re-apply your change.
                <div style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => {
                      setConflict(false);
                      void reload().then((fresh) => onChanged({ eventId, status: fresh.status }));
                    }}
                  >
                    Reload event
                  </button>
                </div>
              </div>
            </div>
          )}
          {flash && !conflict && (
            <div style={{ padding: '12px 18px 0' }}>
              <div className="note">{flash}</div>
            </div>
          )}
          {twinWarn && (
            <div style={{ padding: '12px 18px 0' }}>
              <div className="note warn" role="alert" data-testid="twin-not-locked">{twinWarn}</div>
            </div>
          )}

          {/* SMS compliance flag (matching v2): a suggestion for the operator, never an auto write. */}
          {saBanner && (
            <div style={{ padding: '12px 18px 0' }}>
              <div className={saBanner.level === 'warn' ? 'note warn' : 'note'}>
                <b>{saBanner.text}</b>
              </div>
            </div>
          )}

          {/* Re-subscribe guard (William feedback #4, 2026-07-16). Its own banner, deliberately NOT
              folded into the STOP banner above: that one needs the poller's ingest-time
              `suggestedAction`, and a tee'd SMS that classified as Email never got one - which is
              the exact shape of the STOP that has already been marked do-not-solicit in error. This
              reads the content at projection time instead, so it fires on both shapes. */}
          {resubscribed && (
            <div style={{ padding: '12px 18px 0' }}>
              <div className="note warn" role="alert">
                <b>Already re-subscribed - do not record an opt-out.</b>{' '}
                This number texted START on {fmtTs(ev.resubscribedStartTs ?? '')}, after this message.
                The SMS platform re-subscribed the member automatically when it arrived, so this STOP
                is no longer what they want. Opt-out of program and Do Not Contact are blocked in the
                disposition list below: recording either marks the member do-not-solicit permanently,
                and no system in the fleet can undo that. If they have since asked to stop again, that
                later message is its own row - work the opt-out there.
              </div>
            </div>
          )}

          {/* 2 · Message / call */}
          <div style={section}>
            <div
              className="block-head sec-head"
              role="button"
              tabIndex={0}
              aria-expanded={messageOpen}
              onClick={() => setMessageOpen((o) => !o)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setMessageOpen((o) => !o);
                }
              }}
              title={messageOpen ? 'Collapse message' : 'Expand message'}
            >
              {/* William 2026-07-14: play-button-sized arrow (supersedes the 2026-07-11 modest-glyph
                  ruling for this drawer's section headers). */}
              <span className="sec-label">
                <SecArrow open={messageOpen} />
                {isOutbound ? 'Call' : 'Message'}
              </span>
            </div>
            {ev.subject && !(messageOpen && ev.channel === 'Email') && <div style={{ fontWeight: 600, margin: '4px 0' }}>{ev.subject}</div>}
            {messageOpen ? (
              // PART Q (William, 17 Oct 2026): an Email case shows the whole message (From, To, Sent, Subject, the
              // body, the earlier messages folded, the attachment names); the stored text is the fallback.
              ev.channel === 'Email' ? (
                <EmailMessage
                  eventId={ev.eventId}
                  fallback={ev.content ? <TranscriptText className="small" text={ev.content} /> : <div className="small"><span className="muted">(no text content)</span></div>}
                />
              ) : ev.content ? (
                <TranscriptText className="small" text={ev.content} />
              ) : (
                <div className="small"><span className="muted">(no text content)</span></div>
              )
            ) : (
              <div
                className="small muted"
                onClick={() => setMessageOpen(true)}
                style={{
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                }}
                title={ev.channel === 'Email' ? 'Click to see the whole email: From, To, Sent, the message and earlier messages' : 'Click to expand the full message'}
                data-testid="message-collapsed"
              >
                {ev.content ? messageFirstLine(ev.content) : ev.channel === 'Email' ? EMAIL_WORDING.empty : '(no text content)'}
              </div>
            )}

            {/* Three-timestamp discipline (Teams audio): interaction is the truth, never play-time. */}
            {(ev.interactionStartTs || ev.captureCreatedTs || ev.teamsCallId) && (
              <div style={{ marginTop: 10 }}>
                {ev.interactionStartTs && (
                  <div className="leader-row">
                    <span className="k">Interaction</span>
                    <span className="leader" />
                    <span className="v">
                      {fmtTs(ev.interactionStartTs)}
                      {ev.recordingDurationSec ? ` · ${fmtDuration(ev.recordingDurationSec)}` : ''}
                    </span>
                  </div>
                )}
                {ev.captureCreatedTs && (
                  <div className="leader-row">
                    <span className="k">Capture started</span>
                    <span className="leader" />
                    <span className="v">{fmtTs(ev.captureCreatedTs)}</span>
                  </div>
                )}
                {ev.timestampSource && (
                  <div className="leader-row">
                    <span className="k">Time source</span>
                    <span className="leader" />
                    <span className="v">{ev.timestampSource}</span>
                  </div>
                )}
              </div>
            )}

            {ev.hasAudio && (
              <div style={{ marginTop: 10 }}>
                <div className="micro" style={{ marginBottom: 4 }}>Recording</div>
                {audioUrl ? (
                  <audio controls src={audioUrl} style={{ width: '100%' }} />
                ) : (
                  <span className="muted small">loading audio...</span>
                )}
              </div>
            )}
            {ev.transcriptRef && (
              <div style={{ marginTop: 10 }}>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => void loadTranscript()}>
                  {showTranscript ? 'Hide transcript' : 'Show transcript'}
                </button>
                {showTranscript && transcript !== null && (
                  // No inner scroll (William 2026-07-14 single-scrollbar ruling): the transcript
                  // flows; the drawer body / pop-out window is the one vertical bar.
                  <TranscriptText
                    className="small"
                    mono
                    text={transcript}
                    style={{ marginTop: 8, background: 'var(--paper-2)', padding: 10, borderRadius: 2 }}
                  />
                )}
              </div>
            )}
          </div>

          {/* Member match + snapshot + typeahead + full record. Roll-up (William 2026-07-14):
              defaults collapsed once matched, with the KEY identity (name + member #) always
              visible at the collapsed level; while unmatched it defaults open (matching is the
              work). The do-not-solicit banner renders OUTSIDE the roll-up so no collapse can
              hide it. */}
          <div style={section}>
            <div
              className="block-head sec-head"
              role="button"
              tabIndex={0}
              aria-expanded={memberExpanded}
              onClick={() => setMemberOpen(!memberExpanded)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setMemberOpen(!memberExpanded);
                }
              }}
              title={memberExpanded ? 'Collapse member detail' : 'Expand member detail'}
            >
              <span className="sec-label">
                <SecArrow open={memberExpanded} />
                {/* R. 81: names the campaign roster this entry's lookups read, so Marny knows which
                    roster a match or a search came from ("26 A1c-KED General roster"). */}
                {ROSTER_WORDING.header(rosterName)}
              </span>
              <span className="aux">
                {ev.matchedBy && ev.matchedBy !== 'none' ? matchedByLabel(ev.matchedBy) : ev.matchConfidence}
                {/* Slice B (img8): the file carries no snapshot, so the name came from the entry's roster. */}
                {ev.memberSnapshotFrom === 'roster' ? ' · read from the roster' : ''}
              </span>
            </div>
            {/* PART B: a Find Member match waiting for approval, named, with its decision. */}
            {pendingFind && (
              <div className="note" data-testid="match-pending" style={{ margin: '4px 0' }}>
                {FIND_MEMBER_WORDING.pending(String(pendingFind.body?.['memberName'] ?? ''), String(pendingFind.body?.['memberId'] ?? ''), FIND_MEMBER_WORDING.decisionWords[(String(pendingFind.body?.['decision'] ?? 'none')) as keyof typeof FIND_MEMBER_WORDING.decisionWords] ?? '')}
              </div>
            )}
            {/* Collapsed summary: the key name + member number stay visible (William 2026-07-14). */}
            {!memberExpanded &&
              (m ? (
                <div
                  style={{ fontWeight: 600, cursor: 'pointer' }}
                  onClick={() => setMemberOpen(true)}
                  title="Expand member detail"
                >
                  {m.full_name} <span className="muted">#{fmtMemberId(m.insurance_id || m.member_id)}</span>
                </div>
              ) : (
                <div className="muted small">No member matched. Expand to search.</div>
              ))}
            {/* Do-not-contact warning (Item A). Positive-only: shown ONLY when 26fobt marks the member
                do-not-solicit or the platform reports an SMS opt-out. Its ABSENCE is never a clearance
                to contact (26fobt.do_not_solicit is known-incomplete). Never inside the roll-up: it must
                be seen before the drawer's Teams click-to-call even when the member detail is collapsed. */}
            {/* Row 286: the notation on the member's record in THIS console, naming the Care Portal user. */}
            {markedDnc && (
              <div className="dnc-banner" role="alert">
                <strong>{DNC_WORDING.kindLabel[String(markedDnc.data?.kind ?? 'dnc')] ?? 'Do not contact'}.</strong>{' '}
                {DNC_WORDING.markedOn(String(markedDnc.data?.kind ?? 'dnc'), markedDnc.byName || markedDnc.by || '', fmtTs(markedDnc.ts ?? ''))}
                {markedDnc.data?.lock === true ? ' Locked.' : ''}
                {markedDnc.data?.reason ? <> Reason: {String(markedDnc.data.reason)}.</> : null}
              </div>
            )}
            {dnc && (dnc.doNotSolicit !== '' || dnc.suppressed) && (
              <div className="dnc-banner" role="alert">
                <strong>Do not solicit.</strong>{' '}
                {dnc.doNotSolicit !== '' && <>26fobt flags this member do-not-solicit (as of {dnc.doNotSolicit}). </>}
                {dnc.suppressed && <>This member has opted out of SMS. </>}
                {/* The platform stamps resubscribedTs on re-subscribe and does NOT clear it when it
                    suppresses again, and this feed carries only ACTIVE suppressions - so a stamp
                    here means the member came back and then left again. Read any other way it would
                    argue for texting a member who is currently opted out. */}
                {dnc.suppressed && dnc.resubscribedTs !== '' && (
                  <>They re-subscribed on {fmtTs(dnc.resubscribedTs)} and have opted out again since. </>
                )}
                Verify before any call or text.
              </div>
            )}
            {memberExpanded && (
              <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              {ev.memberId && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={detailLoading}
                  onClick={() => void toggleFullRecord()}
                >
                  {detailLoading ? 'Loading full record...' : showFull ? 'Hide full record' : 'Full record'}
                </button>
              )}
            </div>

            {/* Typeahead search: find and manually attach ANY member (match manual on pick). William's
                console review (2 Oct 2026, image 12: "Remove this.") took it off a matched case; it
                stays on an UNMATCHED case only, the one way to attach a caller whose number is not on
                file (put to him as an idea). */}
            {!ev.memberId && (
              <>
            <div className="field">
              <label className="field-label" htmlFor="member-search">Search members</label>
              <input
                id="member-search"
                className="text-input"
                type="search"
                value={memberQ}
                disabled={locked}
                onChange={(e) => setMemberQ(e.target.value)}
                placeholder="name / date of birth / member ID / phone (any format)"
              />
            </div>
            {memberQ.trim().length >= 2 && memberCands.length === 0 && memberSearchEmpty && memberSearchEmpty.q === memberQ.trim() && (
              <div className="small muted" role="status" data-testid="member-search-empty" style={{ marginBottom: 8 }}>
                {memberSearchEmpty.unread
                  ? MEMBER_SEARCH_WORDING.unread(clientDefs.find((c) => c.slug === ev.client)?.shortName || clientDefs.find((c) => c.slug === ev.client)?.displayName || ev.client)
                  : MEMBER_SEARCH_WORDING.none(memberSearchEmpty.q)}
              </div>
            )}
            {memberQ.trim().length >= 2 && memberCands.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
                {memberCands.map((c) => (
                  <button
                    key={c.member_id}
                    type="button"
                    className="chip"
                    style={{ justifyContent: 'flex-start', textAlign: 'left' }}
                    disabled={busy === 'match' || locked}
                    onClick={() =>
                      void mutate('match', () => matchOne(eventId, c.member_id, pickCampaign(rosterCampaign, c)), (d) => {
                        setMemberQ('');
                        setMemberCands([]);
                        return `Member matched (#${fmtMemberId(String(d.memberId ?? c.member_id))}).`;
                      })
                    }
                  >
                    {c.full_name} <span className="muted">#{fmtMemberId(c.member_id)}{c.dob ? ` · ${c.dob}` : ''}{c.phone1 ? ` · ${c.phone1}` : ''}{c.campaigns?.length ? ` · ${c.campaigns.join(', ')}` : ''}</span>
                  </button>
                ))}
              </div>
            )}
              </>
            )}

            {m ? (
              <div>
                <div style={{ fontWeight: 600 }}>
                  {m.full_name} <span className="muted">#{fmtMemberId(m.insurance_id || m.member_id)}</span>
                </div>
                <div style={{ marginTop: 6 }}>
                  <div className="leader-row"><span className="k">DOB</span><span className="leader" /><span className="v">{m.dob}</span></div>
                  <div className="leader-row"><span className="k">Language</span><span className="leader" /><span className="v">{m.language}</span></div>
                  <div className="leader-row"><span className="k">Address</span><span className="leader" /><span className="v">{[m.city, m.state, m.zip].filter(Boolean).join(', ')}</span></div>
                  <div className="leader-row"><span className="k">Plan</span><span className="leader" /><span className="v">{[m.lob, m.market, m.cohort].filter(Boolean).join(' · ')}</span></div>
                  <div className="leader-row"><span className="k">MSO / IPA</span><span className="leader" /><span className="v">{[m.mso, m.ipa].filter(Boolean).join(' / ')}</span></div>
                  <div className="leader-row"><span className="k">Kit stage</span><span className="leader" /><span className="v">{m.kit_stage}</span></div>
                  <div className="leader-row">
                    <span className="k">Phone</span><span className="leader" />
                    <span className="v">
                      {/* Set-dedupe: rosters commonly carry phone2 == phone1 (duplicate keys + a
                          doubled number otherwise - caught driving the demo). */}
                      {/* Text appears on the FIRST roster number and only when the event carries no
                          from-phone, because that is exactly when the composer falls back to it
                          (sms-send: an explicit phone wins, else phone1 then phone2). On a referral,
                          which has no from-phone at all, this is the operator's Text button. */}
                      {[...new Set([m.phone1, m.phone2].filter(Boolean))].map((p, i) => (
                        <span key={p}>
                          {i > 0 && ' / '}
                          <PhoneActions
                            phone={p}
                            compact
                            blocked={receiveOnly || (!!dnc && (dnc.doNotSolicit !== '' || dnc.suppressed))}
                            checkBeforeCall={{ eventId: ev.eventId, line: rosterLineOf(m, p) }}
                            {...(!ev.fromPhone && i === 0 ? { onText: jumpToCompose } : {})}
                          />
                        </span>
                      ))}
                    </span>
                  </div>
                  <div className="leader-row"><span className="k">Email</span><span className="leader" /><span className="v">{m.email}</span></div>
                  <div className="leader-row"><span className="k">Last activity</span><span className="leader" /><span className="v">{m.last_activity}</span></div>
                </div>
                {showFull && fullDetail && (
                  <div style={{ marginTop: 10, padding: 10, background: 'var(--paper-2)', borderRadius: 2 }}>
                    <div className="micro" style={{ marginBottom: 6 }}>
                      {ROSTER_WORDING.fullRecord(rosterLabel(fullDetail.campaign ?? rosterCampaign, referenceCampaign))} · access reason: handling {ev.eventId}
                    </div>
                    <MemberDetailPanel detail={fullDetail} />
                  </div>
                )}
                {/* Unmatch (William feedback #3, 2026-07-14): a wrong match can be REMOVED without
                    naming a replacement - the event returns to Unmatched, and the known-contact
                    binding this event taught (if it points at this member) is deactivated so the
                    next auto sweep cannot re-derive the same wrong answer. */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
                  {!unmatchConfirm ? (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      disabled={locked || busy === 'match'}
                      onClick={() => setUnmatchConfirm(true)}
                    >
                      Unmatch (wrong member)
                    </button>
                  ) : (
                    <>
                      <span className="small">Remove this match? The event returns to Unmatched - no replacement needed.</span>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        disabled={locked || busy === 'match'}
                        onClick={() => {
                          setUnmatchConfirm(false);
                          void mutate('match', () => matchClear(eventId), 'Match removed - the event is Unmatched.');
                        }}
                      >
                        Remove match
                      </button>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setUnmatchConfirm(false)}>
                        Keep
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : ev.matchCandidates.length > 0 ? (
              <div className="small">
                <div className="muted" style={{ marginBottom: 6 }}>
                  {ev.matchConfidence === 'ambiguous'
                    ? 'Ambiguous - pick the member:'
                    : 'Suggested by name (not a match) - pick to match manually:'}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {ev.matchCandidates.map((c) => {
                    const info = candInfo.get(c);
                    return (
                      <button
                        key={c}
                        type="button"
                        className="chip"
                        style={{ justifyContent: 'flex-start', textAlign: 'left' }}
                        disabled={busy === 'match' || locked}
                        onClick={() => void mutate('match', () => matchOne(eventId, c), 'Member matched.')}
                      >
                        {info?.fullName ? (
                          <>
                            {info.fullName}{' '}
                            <span className="muted">
                              #{fmtMemberId(c)}
                              {info.dob ? ` · DOB ${info.dob}` : ''}
                              {info.lastActivity ? ` · last activity ${info.lastActivity}` : ''}
                            </span>
                            {info.sharedPhone ? <span className="pill pill-warning" style={{ marginLeft: 6 }}>shared phone</span> : null}
                          </>
                        ) : (
                          <>#{fmtMemberId(c)}</>
                        )}
                      </button>
                    );
                  })}
                </div>
                {ev.matchCandidateInfo?.some((i) => i.sharedPhone) && (
                  <div className="micro muted" style={{ marginTop: 4 }}>
                    These members share one phone number - confirm identity before picking.
                  </div>
                )}
              </div>
            ) : (
              <div className="muted small">
                No member matched. Search and pick a member above.
              </div>
            )}
              </>
            )}
          </div>

          {/* 5 · PCP panel (shows when matched). Roll-up (William 2026-07-14): collapsed by default -
              the PCP is workflow context, not something to see all of the time - and the collapsed
              header keeps the PCP name as its summary. Auto-opens when the event is fax-shaped
              (fax referral, re-fax checked, correction in play); the fax tools inside keep their
              own rev7 reveal. */}
          {m && (() => {
            const faxReferral = /fax/i.test(ev.referral?.reason ?? '');
            const faxShaped = faxReferral || checked.has('pcp-fax-resend') || faxCorrected || !!ev.pcpFaxCorrected;
            const pcpExpanded = pcpOpen ?? faxShaped;
            const faxToolsVisible = faxToolsOpen || faxShaped;
            return (
              <div style={section}>
                <div
                  className="block-head sec-head"
                  role="button"
                  tabIndex={0}
                  aria-expanded={pcpExpanded}
                  onClick={() => setPcpOpen(!pcpExpanded)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setPcpOpen(!pcpExpanded);
                    }
                  }}
                  title={pcpExpanded ? 'Collapse PCP panel' : 'Expand PCP panel'}
                >
                  <span className="sec-label">
                    <SecArrow open={pcpExpanded} />
                    PCP
                  </span>
                  <span className="aux">
                    {pcpExpanded
                      ? ev.pcpFaxCorrected
                        ? 'fax corrected'
                        : ''
                      : [m.pcp_name || '-', ev.pcpFaxCorrected ? 'fax corrected' : ''].filter(Boolean).join(' · ')}
                  </span>
                </div>
                {pcpExpanded && (
                  <>
                <div className="leader-row"><span className="k">Name</span><span className="leader" /><span className="v">{m.pcp_name || '-'}</span></div>
                <div className="leader-row"><span className="k">Phone</span><span className="leader" /><span className="v">{m.pcp_phone || '-'}</span></div>
                <div className="leader-row"><span className="k">Fax on file</span><span className="leader" /><span className="v">{m.pcp_fax || '-'}</span></div>
                {ev.pcpFaxCorrected && (
                  <div className="leader-row"><span className="k">Corrected fax</span><span className="leader" /><span className="v">{ev.pcpFaxCorrected}</span></div>
                )}
                {!faxToolsVisible && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    style={{ marginTop: 8 }}
                    disabled={locked || nothingClose}
                    onClick={() => setFaxToolsOpen(true)}
                  >
                    Fax tools (correct the number / re-fax results)
                  </button>
                )}
                {faxToolsVisible && (
                  <>
                    <div className="field" style={{ marginTop: 8 }}>
                      <label className="field-label" htmlFor="pcp-fax">Fax number (used by the re-fax)</label>
                      <input
                        id="pcp-fax"
                        className="text-input"
                        value={pcpFax}
                        disabled={locked || nothingClose}
                        onChange={(e) => setPcpFax(e.target.value)}
                        placeholder="PCP fax number"
                      />
                    </div>
                    <label className="small" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input
                        type="checkbox"
                        checked={faxCorrected}
                        disabled={locked || nothingClose || inReview}
                        onChange={(e) => setFaxCorrected(e.target.checked)}
                      />
                      Fax number corrected (recorded as a PCP fax action; the resolution updates the PCP directory)
                    </label>
                    {/* The office's decision on his answer (2 Oct 2026 2:24 AM, item 6): "Re-fax results to PCP"
                        left the action list; the fax tools' own "Initiate re-fax now" carries the re-fax.
                        Ticking it stages the SAME pcp-fax-resend action the list's checkbox did (the re-fax
                        happens at approval); unticking it un-stages it. */}
                    <label className="small" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                      <input
                        type="checkbox"
                        aria-label="Initiate re-fax now"
                        checked={checked.has('pcp-fax-resend')}
                        disabled={locked || nothingClose || inReview}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setInitiateRefax(true);
                          setChecked((prev) => {
                            const next = new Set(prev);
                            if (on) next.add('pcp-fax-resend');
                            else next.delete('pcp-fax-resend');
                            return next;
                          });
                        }}
                      />
                      Initiate re-fax now (re-faxes the results to this number at approval)
                    </label>
                      {checked.has('pcp-fax-resend') && (
                        <div className="micro muted">
                          Re-fax results to PCP: fax {pcpFax.trim() || '(none entered)'}
                          {faxCorrected ? ' · marked corrected' : ''}
                          {initiateRefax ? ' · re-fax will be initiated' : ' · number update only (no re-fax)'}
                        </div>
                      )}
                      {checked.has('pcp-fax-resend') && faxWarning && (
                        <div
                          role="alert"
                          className="small"
                          style={{ marginTop: 6, padding: '6px 8px', borderRadius: 6, background: 'var(--warn-bg, #fff7e6)', border: '1px solid var(--warn-border, #f0c36d)' }}
                        >
                          <div style={{ fontWeight: 600 }}>No fax will be sent</div>
                          <div>{FAX_NOT_FAXABLE_WORDING.message[faxWarning.reason ?? 'unknown'](faxWarning)}</div>
                          {faxWarning.confirmable ? (
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                              <input
                                type="checkbox"
                                checked={notFaxableConfirmed}
                                disabled={locked}
                                onChange={(e) => setNotFaxableConfirmed(e.target.checked)}
                              />
                              {FAX_NOT_FAXABLE_WORDING.confirmLabel}
                            </label>
                          ) : (
                            <div className="micro" style={{ marginTop: 4 }}>{FAX_NOT_FAXABLE_WORDING.blockedLabel}</div>
                          )}
                        </div>
                      )}
                    {autoFaxCorrection && (
                      <div className="note" style={{ marginTop: 6 }}>
                        Send to review will include the corrected fax number as a number-update-only
                        PCP fax action (no re-fax), so the correction reaches the Portal. Tick
                        "Initiate re-fax now" to also re-fax the results.
                      </div>
                    )}
                  </>
                )}
                  </>
                )}
              </div>
            );
          })()}

          {/* SMS conversation. Keyed on the MEMBER (or the raw phone when unmatched), never on this
              event: the member sees one thread and cannot address a reply to one agent, so the dialog
              belongs beside the event rather than inside it. Every operator working this member sees
              the same transcript, including each other's messages. `contextEventId` is provenance
              only - it records which event was open when a message was sent, and groups nothing.

              Roll-up: OPEN by default (William 2026-08-11, after Marny reported texting as "not
              available" while working a case - the compose box is in here, and on a Voice or
              Referral event this section used to start collapsed). An operator who collapses it
              keeps it collapsed, across events and sessions. The panel stays MOUNTED while rolled up
              (display:none): its live load also feeds the do-not-solicit banner in the Member
              section (Item A), and a collapse must never suppress that signal. */}
          {(ev.memberId || ev.fromPhone) && (() => {
            const smsExpanded = smsOpen ?? true;
            return (
            <div style={section}>
              <div
                className="block-head sec-head"
                role="button"
                tabIndex={0}
                aria-expanded={smsExpanded}
                onClick={() => setSmsOpen(!smsExpanded)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSmsOpen(!smsExpanded);
                  }
                }}
                title={smsExpanded ? 'Collapse the conversation' : 'Expand the conversation'}
              >
                <span className="sec-label">
                  <SecArrow open={smsExpanded} />
                  Conversation
                </span>
                <span className="aux">{m?.full_name || ev.fromPhone || ''}</span>
              </div>
              <div style={smsExpanded ? undefined : { display: 'none' }}>
                {receiveOnly ? (
                  // L3 (Care's audit, 4 Oct 2026; Client Wall rule 1): the texting platform's thread is
                  // read by phone, never by client, so for a receive-only plan it would show ANOTHER
                  // client's texts. Nothing is asked; the server refuses the read as well.
                  <p className="muted" data-testid="receive-only-texts">{RECEIVE_ONLY_WORDING.texts}</p>
                ) : (
                  <SmsConversation
                    {...(ev.memberId ? { memberId: ev.memberId } : {})}
                    {...(ev.fromPhone ? { phone: ev.fromPhone } : {})}
                    {...(ev.memberId && rosterCampaign ? { campaign: rosterCampaign } : {})}
                    contextEventId={ev.eventId}
                    me={myEmail}
                    onLoad={setDnc}
                    focusSignal={textFocusSeq}
                    readOnly={archived}
                  />
                )}
              </div>
            </div>
            );
          })()}

          {/* EMAIL (M1 part B, his R. 184, 4 Oct 2026: Marny may email members; she answers WyoBlue's
              email). A member's OWN email is answered by email from the plan's OWN mailbox, in the same
              conversation, with the member's message kept beneath the reply. Shown only when the
              deployment has member email on, the API named this plan's mailbox, and the entry is the
              member's own email; the server re-checks all three and the STOP check before anything goes. */}
          {replyMailbox && memberEmailEntry(ev) && (
            <div style={section} data-testid="drawer-email-section">
              <div className="block-head">
                Email
                <span className="aux">from {replyMailbox}</span>
              </div>
              <EmailReplyPanel
                ev={ev}
                mailbox={replyMailbox}
                locked={locked || TERMINAL_FOR_KEYS.includes(ev.status)}
                onSent={async () => {
                  const f = await reload();
                  onChanged({ eventId, status: f.status });
                }}
              />
            </div>
          )}
          {/* Notes - ABOVE attachments and sized for real note-taking (William 2026-07-14). The
              Contact log and Requests sections that used to sit here were removed the same day
              (envelope-compatible: contactLog simply rides empty; requests never rode it). Each box
              commits on its own Save (William 2026-07-16) - listening to a call and writing down what
              was said, or leaving a note for a team-mate, is reviewing, and reviewing must not claim
              the event; the API keeps the auto-claim for every other write on this route.

              INTERNAL vs PORTAL (William feedback #1, 2026-07-14): the running notes are internal -
              they never ride the resolution envelope. Sharing with the Portal is a deliberate act:
              text written in the separate "Note to Portal" box below rides the envelope's notes
              field at approval; blank means nothing is shared. */}
          <div style={section}>
            <div className="block-head">
              Notes
              <span className="aux">internal - never sent to the Portal</span>
            </div>
            <textarea
              className="textarea-input"
              aria-label="Notes"
              rows={10}
              style={{ minHeight: 200, resize: 'vertical' }}
              value={notes}
              // Disabled while an attachment upload is in flight: the per-file reload() re-hydrates
              // `notes` from the server, which would otherwise clobber text typed during the upload.
              disabled={locked || busy === 'attach'}
              onChange={(e) => setNotes(e.target.value)}
            />
            <div style={saveRow}>
              {notesDirty && <span className="micro muted">Unsaved</span>}
              <button
                type="button"
                className="btn btn-sm"
                disabled={!notesDirty || locked || busy === 'attach' || busy === 'notes'}
                onClick={() => void mutate('notes', () => saveNotes(eventId, { notes }), 'Notes saved.')}
              >
                {busy === 'notes' ? 'Saving...' : 'Save notes'}
              </button>
            </div>
            <div className="field" style={{ marginTop: 10 }}>
              <label className="field-label" htmlFor="portal-note">Note to Portal (optional)</label>
              <textarea
                id="portal-note"
                className="textarea-input"
                aria-label="Note to Portal"
                rows={3}
                style={{ resize: 'vertical' }}
                value={portalNote}
                disabled={locked || busy === 'attach'}
                onChange={(e) => setPortalNote(e.target.value)}
              />
              <div style={saveRow}>
                {portalNoteDirty && <span className="micro muted">Unsaved</span>}
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={!portalNoteDirty || locked || busy === 'attach' || busy === 'portalNote'}
                  onClick={() => void mutate('portalNote', () => saveNotes(eventId, { portalNote }), 'Portal note saved.')}
                >
                  {busy === 'portalNote' ? 'Saving...' : 'Save note'}
                </button>
              </div>
              <div className="micro muted" style={{ marginTop: 4 }}>
                Rides to the Portal with the resolution at approval. Blank = nothing is shared.
              </div>
            </div>
          </div>

          {/* 8 · Attachments */}
          <div style={section}>
            <div className="block-head">
              Attachments
              <span className="aux">{ev.attachments?.length ? `${ev.attachments.length} on file` : 'none'}</span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                ref={attInputRef}
                type="file"
                multiple
                aria-label="Files to attach"
                disabled={locked || busy === 'attach'}
                className="small"
                style={{ maxWidth: 260 }}
              />
              <select
                className="select-input"
                aria-label="Attachment kind"
                style={{ maxWidth: 140 }}
                value={attKind}
                disabled={locked}
                onChange={(e) => setAttKind(e.target.value as EventAttachment['kind'])}
              >
                {ATTACHMENT_KINDS.map((k) => (
                  <option key={k.value} value={k.value}>{k.label}</option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-sm"
                disabled={locked || busy === 'attach'}
                onClick={() => void runUpload()}
              >
                {busy === 'attach' ? 'Uploading...' : 'Upload'}
              </button>
            </div>
            <div className="micro muted" style={{ marginTop: 4 }}>
              Recordings, consent emails, images, documents. They go to the Portal with the case when it is approved from the Approval Queue. 50 MB cap per file.
            </div>

            {ev.attachments && ev.attachments.length > 0 && (
              <table className="dt" style={{ marginTop: 10 }}>
                <thead>
                  <tr>
                    <th>File</th>
                    <th>Kind</th>
                    <th>Size</th>
                    <th>Uploaded</th>
                    <th>By</th>
                  </tr>
                </thead>
                <tbody>
                  {ev.attachments.map((a) => (
                    <Fragment key={a.id}>
                      <tr>
                        <td>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            style={{ padding: '0 4px' }}
                            onClick={() => void viewAttachment(a)}
                          >
                            {attPreview?.ref === a.ref ? '▾' : '▸'} {a.name}
                          </button>
                        </td>
                        <td>{a.kind}</td>
                        <td className="num-cell">{fmtBytes(a.size)}</td>
                        <td className="small" style={{ whiteSpace: 'nowrap' }}>{fmtTs(a.ts)}</td>
                        <td className="small">{a.byName ?? ''}</td>
                      </tr>
                      {attPreview?.ref === a.ref && (
                        <tr>
                          <td colSpan={5}>
                            <div style={{ margin: '4px 0 8px' }}>
                              {attPreview.media === 'audio' && <audio controls src={attPreview.url} style={{ width: '100%' }} />}
                              {attPreview.media === 'image' && (
                                <img src={attPreview.url} alt={attPreview.name} style={{ maxWidth: '100%', maxHeight: 260, borderRadius: 2 }} />
                              )}
                              {attPreview.media === 'file' && (
                                <a className="btn btn-ghost btn-sm" href={attPreview.url} download={attPreview.name}>
                                  Download {attPreview.name}
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Handoff ledger (the emitted resolution rides here after approval) */}
          {ev.handoffs && ev.handoffs.length > 0 && (
            <div style={section}>
              <div className="block-head">
                Handoffs to Portal
                <span className="aux">{ev.handoffs.length} emitted</span>
              </div>
              {ev.handoffs.map((h, i) => (
                <div className="leader-row" key={`${h.ref}-${i}`}>
                  <span className="k">{h.type}</span>
                  <span className="leader" />
                  <span className="v">{h.status || 'sent'} · {fmtTs(h.ts)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Action / disposition (item 12, William 2026-07-08): the operator's good-faith decision of
              what to DO (may differ from the member's request in item 6). Multi-select; drives the
              Final Review pills and rides to the Portal at review approval. "Nothing / close matter"
              closes the event with no Portal action. */}
          <div style={section}>
            <div className="block-head">
              Action / disposition
              <span className="aux">what to do · drives review + rides to the Portal at approval</span>
            </div>
            {inReview && (
              <div className="micro muted" style={{ marginBottom: 6 }}>
                In review - dispositions are frozen (a new checkmark here would have no way to save).
                Use Recall from review below to change them; notes stay editable.
              </div>
            )}
            {resubscribed && (
              <div className="micro muted" style={{ marginBottom: 6 }}>
                Opt-out of program and Do Not Contact are blocked - this number texted START on{' '}
                {fmtTs(ev.resubscribedStartTs ?? '')}, after this message. See the banner at the top.
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {/* His map (2 Oct 2026, image 14): Change information, Opting, Kits, Resulting, Consent,
                  then No action; the items his map does not name sit folded under Other. */}
              {ACTION_GROUPS.map((g) => {
                // L1 (William, 16 Oct 2026 about 10 PM): the keys show only when the server says the master
                // link is on; off, the server refuses each with a 409, so they are hidden.
                const keys = groupKeysFor(g.label, features.masterDecisions, features.unstopCareLine);
                return (
                <div key={g.label} style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-testid={`action-group-${g.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="micro" style={{ marginTop: 4 }}>{g.label}</div>
                  {g.types.map((t) => (
                <div key={t}>
                  {/* Light-up toggle (William, 16 Oct 2026: "make these like light up buttons that glow when
                      chosen (not animated)"): press once and it turns green, press again and it turns off.
                      Same state, handler and guards as the checkbox it replaced; only the look changed. */}
                  <div className="small" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="dispo-toggle"
                      aria-pressed={checked.has(t)}
                      data-testid={`dispo-toggle-${t}`}
                      disabled={locked || nothingClose || inReview || blockedByResubscribe(t) || (t === 'result-letter-resend' && !!letterBlocked)}
                      onClick={() => toggleAction(t)}
                    >
                      {ACTION_META[t].label}
                    </button>
                    {t === 'pcp-fax-resend' && !m ? <span className="micro muted">(needs a matched member)</span> : null}
                    {blockedByResubscribe(t) ? <span className="micro muted">(blocked - already re-subscribed)</span> : null}
                  </div>
                  {t === 'result-letter-resend' && letterBlocked && (
                    <div className="note warn" role="note" style={{ margin: '4px 0 4px 22px' }} data-testid="letter-blocked">{letterBlocked}</div>
                  )}
                  {checked.has(t) && (
                    <div style={{ margin: '4px 0 4px 22px' }}>
                      {t === 'change-of-address' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <input className="text-input" aria-label="Updated address line 1" value={addr.line1} disabled={locked} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} placeholder="Updated address line 1" />
                          <input className="text-input" aria-label="Updated address line 2" value={addr.line2} disabled={locked} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} placeholder="Line 2 (optional)" />
                          <div style={{ display: 'flex', gap: 4 }}>
                            <input className="text-input" aria-label="City" value={addr.city} disabled={locked} onChange={(e) => setAddr({ ...addr, city: e.target.value })} placeholder="City" />
                            <input className="text-input" aria-label="State" style={{ maxWidth: 70 }} value={addr.state} disabled={locked} onChange={(e) => setAddr({ ...addr, state: e.target.value })} placeholder="ST" />
                            <input className="text-input" aria-label="Zip" style={{ maxWidth: 100 }} value={addr.zip} disabled={locked} onChange={(e) => setAddr({ ...addr, zip: e.target.value })} placeholder="Zip" />
                          </div>
                        </div>
                      )}
                      {t === 'consent-update' && (
                        <select className="select-input" aria-label="Consent state" value={consentOnFile ? 'on-file' : 'none'} disabled={locked} onChange={(e) => setConsentOnFile(e.target.value === 'on-file')}>
                          <option value="on-file">Consent on file</option>
                          <option value="none">No consent</option>
                        </select>
                      )}
                      {t === 'opt-out' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <div className="micro muted">
                            This program's communications only. For a permanent stop across everything, use Do Not Contact (DNC).
                          </div>
                          {/* I1 a (William, 17 Oct 2026): the stop waits for approval; its texting block goes on at once. */}
                          {optOutScope !== 'email' && (
                            <div className="micro muted" data-testid="optout-waits">{STOP_WAITS_WORDING.optOutDisposition}</div>
                          )}
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <select className="select-input" aria-label="Opt-out scope" style={{ maxWidth: features.optOutEmail ? 170 : 110 }} value={optOutScope} disabled={locked} onChange={(e) => setOptOutScope(e.target.value as OptOutScope)}>
                              {optOutScopes.map((sc) => (
                                <option key={sc} value={sc}>{OPT_OUT_SCOPE_LABEL[sc] ?? sc}</option>
                              ))}
                            </select>
                            <select className="select-input" aria-label="Opt-out reason" value={optOutReason} disabled={locked} onChange={(e) => setOptOutReason(e.target.value)}>
                              <option value="">Reason (reported to Alignment)...</option>
                              {OPT_OUT_REASONS.map((r) => (
                                <option key={r.value} value={r.value}>{r.label}</option>
                              ))}
                            </select>
                          </div>
                          {optOutReason === 'other' && (
                            <input className="text-input" aria-label="Opt-out reason (other)" value={optOutReasonOther} disabled={locked} onChange={(e) => setOptOutReasonOther(e.target.value)} placeholder="Reason in your own words" />
                          )}
                          {/* M1-C: what the two new options do, said only when one is chosen. */}
                          {optOutScope === 'email' && (
                            <div className="micro muted" data-testid="optout-email-note">
                              Email only: a stop on email, on the member's file, written when Final Review approves the case. Nothing is sent to the texting platform.
                            </div>
                          )}
                          {optOutScope === 'campaign' && (
                            <div className="micro muted" data-testid="optout-campaign-note">
                              {rosterCampaign
                                ? `This campaign only: ${rosterCampaign}. The member stays contactable for every other campaign.`
                                : 'This entry names no campaign: set the campaign in the drawer first.'}
                            </div>
                          )}
                        </div>
                      )}
                      {t === 'decline' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <div className="micro muted">
                            {DECLINE_WORDING.choice.decline}. Use Do Not Contact (DNC) only when the
                            member {DECLINE_WORDING.choice.stop.toLowerCase()}.
                          </div>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <select className="select-input" aria-label="Decline scope" style={{ maxWidth: 190 }} value={declineScope} disabled={locked} onChange={(e) => setDeclineScope(e.target.value as (typeof DECLINE_SCOPES)[number])}>
                              {DECLINE_SCOPES.map((sc) => (
                                <option key={sc} value={sc}>{DECLINE_WORDING.scope[sc]}</option>
                              ))}
                            </select>
                            <input className="text-input" aria-label="Decline reason" value={declineReason} disabled={locked} onChange={(e) => setDeclineReason(e.target.value)} placeholder={DECLINE_WORDING.reasonRequired} />
                          </div>
                        </div>
                      )}
                      {t === 'do-not-contact' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <div className="micro muted">
                            {DNC_WORDING.markScope} Goes on the master list and the member's record with your name.
                          </div>
                          {/* I1 a (William, 17 Oct 2026): the stop waits for approval; its block goes on at once. */}
                          <div className="micro muted" data-testid="dnc-waits">{STOP_WAITS_WORDING.dncDisposition}</div>
                          <input className="text-input" aria-label="Do Not Contact reason" value={dncDetail} disabled={locked} onChange={(e) => setDncDetail(e.target.value)} placeholder="Reason (required)" />
                        </div>
                      )}
                      {t === 'opt-in' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <div className="micro muted" data-testid="opt-in-waits">{STAGED_WORDING.optInWaits}</div>
                          <input className="text-input" aria-label="Opt-in note" value={optInDetail} disabled={locked} onChange={(e) => setOptInDetail(e.target.value)} placeholder="Note (optional, e.g. wants to receive a kit)" />
                        </div>
                      )}
                      {t === 'test-completed' && (
                        <select className="select-input" aria-label="Test completed method" value={testMethod} disabled={locked} onChange={(e) => setTestMethod(e.target.value)}>
                          <option value="">How was it completed...</option>
                          {TEST_COMPLETED_METHODS.map((x) => (
                            <option key={x.value} value={x.value}>{x.label}</option>
                          ))}
                        </select>
                      )}
                      {t === 'general-inquiry' && (
                        <input className="text-input" aria-label="General inquiry note" value={genInquiryDetail} disabled={locked} onChange={(e) => setGenInquiryDetail(e.target.value)} placeholder="What was asked (optional)" />
                      )}
                      {t === 'referred-to-client-services' && (
                        <input className="text-input" aria-label="Referred to client member services note" value={referredDetail} disabled={locked} onChange={(e) => setReferredDetail(e.target.value)} placeholder="Note (optional)" />
                      )}
                      {t === 'replacement-kit' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <select className="select-input" aria-label="Replacement kit reason" value={kitReason} disabled={locked} onChange={(e) => setKitReason(e.target.value)}>
                            <option value="">Reason...</option>
                            {REPLACEMENT_KIT_REASONS.map((r) => (
                              <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                          </select>
                          {kitReason === 'other' && (
                            <input className="text-input" aria-label="Replacement kit reason (other)" value={kitReasonOther} disabled={locked} onChange={(e) => setKitReasonOther(e.target.value)} placeholder="Reason in your own words" />
                          )}
                        </div>
                      )}
                      {t === 'result-letter-resend' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <div className="micro muted">
                            {m
                              ? `Sends to the current address on file: ${[m.address, m.city, m.state, m.zip].filter(Boolean).join(', ') || '(no address on file)'}`
                              : 'Match a member to see the address the letter is sent to.'}
                          </div>
                          <input className="text-input" aria-label="Result letter resend note" value={letterNote} disabled={locked} onChange={(e) => setLetterNote(e.target.value)} placeholder="Note (optional)" />
                          <input className="text-input" aria-label="Original letter record ID" value={letterOrigId} disabled={locked} onChange={(e) => setLetterOrigId(e.target.value)} placeholder="Original letter record ID (leave blank; a fresh letter is built from the member record)" />
                        </div>
                      )}
                      {t === 'callback-scheduled' && (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          <input type="date" className="text-input" aria-label="Follow-up call date" style={{ maxWidth: 170 }} value={callbackDate} disabled={locked} onChange={(e) => setCallbackDate(e.target.value)} />
                          <input type="time" className="text-input" aria-label="Follow-up call time" style={{ maxWidth: 130 }} value={callbackTime} disabled={locked} onChange={(e) => setCallbackTime(e.target.value)} />
                        </div>
                      )}
                      {t === 'special-case' && (
                        <textarea className="textarea-input" aria-label="Report to plan description" rows={2} value={specialDesc} disabled={locked} onChange={(e) => setSpecialDesc(e.target.value)} placeholder="Information to report to the plan" />
                      )}
                    </div>
                  )}
                </div>
                  ))}
                  {/* His answers (2 Oct 2026, idea 3: "Return all of them (put them where they belong)"):
                      the keys with no checkbox sit in their groups (Change language; Unstop and Un-opt-out;
                      the explainer video under Kits, his map 2.3.2), each the same quick-key press as before. */}
                  {keys && !archived && (
                    <QuickKeys
                      ev={ev}
                      locked={locked || TERMINAL_FOR_KEYS.includes(ev.status)}
                      replyButtons={features.replyButtons}
                      replyBusy={busy.startsWith('reply-')}
                      onReplyPress={() => undefined}
                      only={keys}
                      unstopCareLine={features.unstopCareLine}
                      pressBlock={pressGuard}
                      onStaged={() => { void reload().then((fresh) => onChanged({ eventId, status: fresh.status })); }}
                    />
                  )}
                </div>
                );
              })}
            </div>
            {/* PART G (William, 16 Oct 2026 about 11:15 PM: "Remove the langauge"): no note while the link is off. */}
            {!archived && features.masterDecisions && (
              <div className="micro muted" style={{ marginTop: 6 }} data-testid="keys-note">
                {keysNote(features.masterDecisions, features.stagedPresses)}
              </div>
            )}
            {/* Wave 3 item 4, the unsaved-ticks guard, said once where the keys are (each key also says it). */}
            {pressGuard && !archived && (
              <div className="note warn" role="note" style={{ marginTop: 6 }} data-testid="press-guard">{pressGuard}</div>
            )}

            <div className="micro" style={{ marginTop: 8 }}>No action</div>
            {/* Nothing / close matter: exclusive disposition - closes the event, rides no Portal action.
                Always toggleable (only locked disables it) so it can never deadlock; CHECKING it clears
                any selected actions AND any pending fax correction, and while it is on the actions, the
                SMS opt-out banner, and the PCP fax tools are all disabled - so nothing can be silently
                dropped on close. */}
            <div className="small" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="dispo-toggle"
                aria-pressed={nothingClose}
                aria-describedby="close-file-explainer"
                data-testid="dispo-toggle-close-file"
                disabled={locked || inReview || queuedClose}
                onClick={() => {
                  const on = !nothingClose;
                  setNothingClose(on);
                  if (on) {
                    setChecked(new Set());
                    setFaxCorrected(false);
                  }
                }}
              >
                Close file
              </button>
              <span id="close-file-explainer" className="micro muted">(answered, no further follow-up: logs the close and closes the case; nothing is sent to the member)</span>
            </div>
            {/* Wave 3 item 4: a Close file already queued on the case is said here, and no second one is offered. */}
            {queuedClose && (
              <div className="micro muted" style={{ marginTop: 4 }} data-testid="close-file-queued">{STAGED_WORDING.closeQueued}</div>
            )}
            {nothingClose && (
              <input
                className="text-input"
                aria-label="Close file note"
                style={{ marginTop: 6 }}
                value={closeNote}
                maxLength={500}
                disabled={locked}
                onChange={(e) => setCloseNote(e.target.value)}
                placeholder="Note (optional)"
              />
            )}

            {/* PART H (William, 16 Oct 2026 about 11:16 PM, on "Mark internal  a CDX staff or test number, never
                matched to a member": "remove"). The control and its line are off the case view. The API route
                (care/internal-numbers) and the internal-numbers list are untouched, so numbers already marked
                stay marked; the queue header's manager still edits the list. */}

            {actionProblems.length > 0 && (checked.size > 0 || faxCorrected) && !nothingClose && (
              <div className="micro muted" style={{ marginTop: 6 }}>{actionProblems.join(' ')}</div>
            )}
            {nothingClose ? (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                style={{ marginTop: 8 }}
                data-testid="close-file-run"
                disabled={busy === 'close' || locked}
                onClick={runCloseMatter}
              >
                {busy === 'close' ? 'Closing...' : 'Close file'}
              </button>
            ) : null}
            {/* PART J (William, 16 Oct 2026 about 11:20 PM, on "1 DISPOSITION SELECTED · SAVED TO THE PORTAL WHEN
                YOU SEND TO REVIEW BELOW.": "remove"). The counter line is gone; what the ticks do is unchanged. */}

            {actionLog.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div className="micro" style={{ marginBottom: 4 }}>Action log</div>
                {actionLog.map((a, i) => {
                  // Wave 3 item 4: the row an approval leaves for each queued press, in words: which
                  // press, applied or REFUSED (with the reason), and who pressed it. Never a bare code.
                  const press = isPressOutcomeAction(a) ? pressOutcomeLine(a) : null;
                  return (
                    <div className="leader-row" key={`${a.type}-${a.ts}-${i}`} {...(press ? { 'data-testid': press.refused ? 'press-refused-row' : 'press-applied-row' } : {})}>
                      <span className="k">{press ? `${press.label}: ${press.detail}` : (ACTION_META[a.type]?.label ?? a.type)}</span>
                      <span className="leader" />
                      <span className="v">{fmtTs(a.ts)}{a.byName ? ` · ${a.byName}` : ''}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Waiting for approval (wave 3 item 4; William, 2 Oct 2026: "all items go to approval then take
              effect"): the presses QUEUED on this case, each with what it is, who pressed it and when.
              None has taken effect. Withdraw takes one off before the case is in review; in review the
              way is Recall from review (or the reviewer's Return to queue), and the block says so. It
              sits beside Final review because that is where the case is sent, recalled and approved.
              Renders nothing when no press is queued, which is always the case with the switch off. */}
          {pending.length > 0 && (
            <div style={section} data-testid="pending-block">
              <div className="block-head">
                {STAGED_WORDING.blockHead}
                <span className="aux">{STAGED_WORDING.blockAux(pending.length)}</span>
              </div>
              <div className="micro muted" style={{ marginBottom: 6 }}>{STAGED_WORDING.blockNote}</div>
              {pending.map((p) => (
                <div className="leader-row" key={p.id} data-testid={`pending-${p.id}`}>
                  <span className="k">{pendingWords(p)}</span>
                  <span className="leader" />
                  <span className="v">
                    {pendingWho(p)} · {fmtTs(p.at)}
                    {canWithdraw && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={{ marginLeft: 8 }}
                        disabled={busy !== ''}
                        title={STAGED_WORDING.withdrawHelp}
                        aria-label={`${STAGED_WORDING.withdraw} ${pendingWords(p)}`}
                        onClick={() => void runWithdraw(p)}
                      >
                        {busy === `withdraw-${p.id}` ? STAGED_WORDING.withdrawing : STAGED_WORDING.withdraw}
                      </button>
                    )}
                  </span>
                </div>
              ))}
              {!canWithdraw && !archived && (
                <div className="micro muted" style={{ marginTop: 6 }} data-testid="pending-no-withdraw">
                  {inReview ? STAGED_WORDING.inReview : claimedByOther ? STAGED_WORDING.lockedNote : STAGED_WORDING.finishedNote}
                </div>
              )}
            </div>
          )}

          {/* 11 · Final review: handling ends at Send to review (no clear button, William #3) */}
          <div style={section}>
            <div className="block-head">
              Final review
              <span className="aux">handling ends here</span>
            </div>
            {ev.status === 'cleared' ? (
              <div className="note">
                <b>Approved.</b> {ev.reviewedBy ? `Reviewed by ${ev.reviewedBy}. ` : ''}
                {ev.resolution ? `Resolution: ${ev.resolution}. ` : ''}
                {ev.reviewBatchId ? `Batch ${ev.reviewBatchId}. ` : ''}
                {ev.clearedTs ? `Cleared ${fmtTs(ev.clearedTs)}.` : ''}
              </div>
            ) : ev.status === 'in_review' ? (
              <>
                {/* William 2026-07-14: after the click the button itself changes color and says so -
                    the same control the operator pressed, now emerald and settled. */}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button type="button" className="btn btn-sm btn-sent" disabled>
                    ✓ Sent to review
                  </button>
                  {/* Recall (William feedback #14): claim-locked; pulls it back to in_progress. */}
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={busy === 'review' || claimedByOther}
                    title={claimedByOther ? `Claimed by ${handlerName}` : 'Pull this event back out of review to keep working it'}
                    onClick={() => void runRecall()}
                  >
                    {busy === 'review' ? 'Recalling...' : 'Recall from review'}
                  </button>
                </div>
                <div className="note" style={{ marginTop: 8 }}>
                  <b>Awaiting review.</b> Sent {ev.sentToReviewTs ? fmtTs(ev.sentToReviewTs) : ''}. The
                  reviewer approves it on the Final review page; approval emits the resolution to the
                  Portal. Notes stay editable until approval and ride with it; to change dispositions,
                  Recall from review.
                </div>
              </>
            ) : (
              <>
                {unmatchedMutating && (
                  <div className="note warn" style={{ marginBottom: 8 }}>
                    <b>Member required.</b> The recorded actions change member data, but no member is
                    matched. Match a member above before sending to review (the Portal rejects
                    unmatched member-mutating resolutions).
                  </div>
                )}
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={busy === 'review' || locked || !sendEligible || !hasWork || unmatchedMutating || actionProblems.length > 0 || nothingClose}
                  title={nothingClose ? 'This case will be closed - use "Close file" in the Action / disposition section above.' : sendBlockedTitle}
                  style={busy === 'review' ? { background: 'var(--teal-2)', borderColor: 'var(--teal-2)', color: '#fff' } : undefined}
                  onClick={() => void runSendToReview()}
                >
                  {busy === 'review' ? APPROVAL_QUEUE_WORDING.sending : APPROVAL_QUEUE_WORDING.button}
                </button>
                {busy === 'review' && <div className="mc-progress" aria-hidden="true" />}
                {!nothingClose && sendEligible && (
                  <div className="micro muted" style={{ marginTop: 6 }}>
                    {APPROVAL_QUEUE_WORDING.help}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Review decision (Final Review page only; claim-exempt server-side) */}
          {/* PART I (William, 16 Oct 2026): a receive-only plan is approved here too; its resolution goes to its own site. */}
          {reviewMode && (ev.status === 'in_review' || ev.status === 'actioned') && (
            <div style={section}>
              <div className="block-head">
                Review decision
                <span className="aux">approval emits the resolution</span>
              </div>
              <div className="field">
                <label className="field-label" htmlFor="review-note">Resolution note</label>
                <textarea
                  id="review-note"
                  className="textarea-input"
                  rows={2}
                  value={reviewNote}
                  onChange={(e) => setReviewNote(e.target.value)}
                  placeholder="How this was resolved (rides the resolution JSON)"
                />
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={busy === 'review'}
                  onClick={() =>
                    // Wave 3 item 4: an approval also runs the presses queued on the case, and a queued
                    // Close file closes it with no resolution. The sentence reads what was queued and what
                    // the answer says the case became; with nothing queued it is the sentence it always was.
                    // QA-1689 F2: a STOP whose envelope revision failed is said plainly, never success alone.
                    // Opt-out master write (MEMBERCARE_OPTOUT_MASTER_WRITE on only): a failed master send is said too.
                    void mutate('review', () => reviewEvent(eventId, 'approve', reviewNote.trim() || undefined), (data) =>
                      [stopEnvelopeFailed(data) ? `${approvedSentence(pending, data)} ${STOP_ENVELOPE_WORDING.failed}` : approvedSentence(pending, data),
                        features.optOutMasterWrite ? masterAnswerWords(data) : ''].filter(Boolean).join(' '))
                  }
                >
                  Approve
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={busy === 'review'}
                  onClick={() =>
                    void mutate('review', () => reviewEvent(eventId, 'return', reviewNote.trim() || undefined), 'Returned to the queue.')
                  }
                >
                  Return to queue
                </button>
              </div>
            </div>
          )}

          {/* QA-1689 F2: a cleared case whose STOP never reached the Portal (its envelope revision failed) offers the
              retry. Approve again sends the STOP on the envelope and approves nothing new (api care/event-review.ts). */}
          {reviewMode && stopEnvelopeMissing(ev) && (
            <div style={section} data-testid="stop-envelope-retry">
              <div className="block-head">{STOP_ENVELOPE_WORDING.head}</div>
              <div className="note warn" style={{ marginBottom: 8 }}>{STOP_ENVELOPE_WORDING.failed}</div>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={busy === 'review'}
                onClick={() =>
                  void mutate('review', () => reviewEvent(eventId, 'approve'), (data) =>
                    stopEnvelopeFailed(data) ? STOP_ENVELOPE_WORDING.failed : STOP_ENVELOPE_WORDING.sent)
                }
              >
                {busy === 'review' ? STOP_ENVELOPE_WORDING.retrying : STOP_ENVELOPE_WORDING.retry}
              </button>
            </div>
          )}

          {/* CARE-08 review R1: a cleared case still holding a block whose lift failed offers the retry. Approve again
              retries only those lifts and changes nothing else (api care/event-review.ts, lib/stop-finalize.ts). */}
          {reviewMode && liftRetryOffered({ ...ev, archived }) && (
            <div style={section} data-testid="stop-lift-retry">
              <div className="block-head">{LIFT_RETRY_WORDING.head}</div>
              <div className="note warn" style={{ marginBottom: 8 }}>{LIFT_RETRY_WORDING.failed}</div>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={busy === 'review'}
                onClick={() => void mutate('review', () => reviewEvent(eventId, 'approve'), (data) => liftRetryAnswer(data))}
              >
                {busy === 'review' ? LIFT_RETRY_WORDING.retrying : LIFT_RETRY_WORDING.retry}
              </button>
            </div>
          )}

          {/* Opt-out master write (William, 22 Oct 2026, BN1 a; MEMBERCARE_OPTOUT_MASTER_WRITE on only): an approved case whose
              decision did not reach the master database. Retryable: the plain sentence and Approve (retry), which resends
              the same batch and approves nothing new (api care/event-review.ts). Refused (QA-1705 A3): the server's
              sentence and no button. Switch off: nothing here. */}
          {reviewMode && features.optOutMasterWrite && masterSendState(ev) && (
            <div style={section} data-testid="master-retry">
              <div className="block-head">{MASTER_RETRY_WORDING.head}</div>
              <div className="note warn" style={{ marginBottom: 8 }}>
                {masterSendState(ev) === 'retry' ? MASTER_RETRY_WORDING.failed : MASTER_RETRY_WORDING.refused}
              </div>
              {masterSendState(ev) === 'retry' && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={busy === 'review'}
                  onClick={() =>
                    void mutate('review', () => reviewEvent(eventId, 'approve'), (data) =>
                      masterAnswerState(data) ? masterAnswerWords(data) : MASTER_RETRY_WORDING.sent)
                  }
                >
                  {busy === 'review' ? MASTER_RETRY_WORDING.retrying : MASTER_RETRY_WORDING.retry}
                </button>
              )}
            </div>
          )}

          {/* Outbound consent capture shortcut (Flow B convenience) */}
          {isOutbound && ev.memberId && (
            <div style={section}>
              <div className="block-head">Consent (Flow B)</div>
              <div className="small muted" style={{ marginBottom: 8 }}>
                Recorded locally; reported to the Portal in the resolution JSON at review approval.
                Never written to 26fobt directly.
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn btn-teal btn-sm"
                  disabled={busy === 'action' || locked}
                  onClick={() => void mutate('action', () => recordActions(eventId, [{ type: 'consent-update', detail: 'consent captured', data: { consentOnFile: true } }]), 'Consent captured.')}
                >
                  Consent captured
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={busy === 'action' || locked}
                  onClick={() => void mutate('action', () => recordActions(eventId, [{ type: 'consent-update', detail: 'member refused', data: { consentOnFile: false } }]), 'Refusal recorded.')}
                >
                  Refused
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={busy === 'callback' || locked}
                  onClick={() => void mutate('callback', () => recordActions(eventId, [{ type: 'callback-scheduled', detail: 'unreachable - retry' }]), 'Call-back scheduled.')}
                >
                  Unreachable
                </button>
              </div>
            </div>
          )}

          {/* 12 · Status history + TAT */}
          {(ev.statusHistory && ev.statusHistory.length > 0) || ev.receivedTs ? (
            <div style={section}>
              <div className="block-head">History</div>
              {(ev.statusHistory ?? []).map((s, i) => (
                <div className="leader-row" key={`${s.status}-${s.ts}-${i}`}>
                  <span className="k">{statusLabel(s.status)}</span>
                  <span className="leader" />
                  <span className="v">{fmtTs(s.ts)}{s.byName ? ` · ${s.byName}` : ''}</span>
                </div>
              ))}
              {ev.receivedTs && ev.responseTs && (
                <div className="leader-row">
                  <span className="k">First response TAT</span>
                  <span className="leader" />
                  <span className="v">{tat(ev.receivedTs, ev.responseTs) || '-'}</span>
                </div>
              )}
              <div className="leader-row">
                <span className="k">Resolution TAT</span>
                <span className="leader" />
                <span className="v">{tatLabel}</span>
              </div>
              {ev.approvedTs && (
                <div className="leader-row">
                  <span className="k">Approved</span>
                  <span className="leader" />
                  <span className="v">{fmtTs(ev.approvedTs)}{ev.reviewedBy ? ` · ${ev.reviewedBy}` : ''}</span>
                </div>
              )}
            </div>
          ) : null}
        </div>
    </>,
  );
}

/**
 * The two physicians on a fax-lane halt, rendered as VALUES (never folded into prose): the operator
 * must tell them apart before she overrides with pcp-fax-resend. "practice not resolved" is the
 * Portal's literal for a historical send whose shared fax number cannot be reverse-resolved and
 * whose row has no NPI; it is a real state and is styled apart from a named practice.
 */
/**
 * Row 232: a text reply from ClariConnect's feed. Shows the class in words, the template answered,
 * and for a texted yes the roster ship-to beside the plan's USPS reading, with the ONE button whose
 * label and effect are row 269's ship path. 'undecided' renders the button disabled and says why.
 */
/**
 * The address a texted yes would ship to: the roster address the agent read at intake, else (the
 * row 269 hand path, a reply the platform could not tie to one member) the member Care matched it to.
 */
function replyShipTo(ev: { smsReply?: SmsReplyBlock | null | undefined; memberSnapshot?: { address?: string; city?: string; state?: string; zip?: string } | null | undefined }): SmsReplyBlock['shipTo'] {
  if (ev.smsReply?.shipTo) return ev.smsReply.shipTo;
  const m = ev.memberSnapshot;
  if (!m?.address) return undefined;
  return { line1: m.address, line2: '', city: m.city ?? '', state: m.state ?? '', zip: m.zip ?? '', source: 'the member Care matched this reply to' };
}

function SmsReplyPanel({
  reply, ship, shipPath, disabled, onConfirm, replyButtons = false, pressDisabled = true, pressNote = '', onPress,
}: {
  reply: SmsReplyBlock;
  ship: SmsReplyBlock['shipTo'];
  shipPath: SmsYesShipPath;
  disabled: boolean;
  onConfirm: (path: SmsYesShipPath) => void;
  /** Row 391: MEMBERCARE_REPLY_BUTTONS is on, so the entry's own presses replace the ship-path button. */
  replyButtons?: boolean;
  pressDisabled?: boolean;
  /** Wave 3 item 4: why the presses wait (unsaved ticks, or a reply press already queued); '' = nothing to say. */
  pressNote?: string;
  onPress?: (op: ReplyOp) => void;
}) {
  const meta = REPLY_CLASS_META[reply.classification] ?? REPLY_CLASS_META.OTHER;
  const wording = SMS_YES_SHIP_WORDING[shipPath];
  // A 1 or a 2 is pressed as what it is; HELP, a question and STOP are MOVED (his words).
  const own = reply.classification === 'YES_KIT' || reply.classification === 'DECLINE_CAMPAIGN';
  const ops = REPLY_OPS.filter((op) => OP_CLASSES[op].includes(reply.classification));
  // William, 17 Oct 2026 1:12 AM ("Remove the move to opted in stuff"): a question or issue (HELP, OTHER)
  // no longer carries "Move to opted in" / "Move to opted out" in the case header. The 1 and 2 answers
  // keep Opt In / Opt Out; STOP keeps Move to do not contact. The routes behind the buttons are untouched.
  const shown = own ? ops : ops.filter((op) => op === 'stop');
  return (
    <div className="micro" style={{ marginTop: 6, display: 'grid', gap: 4 }} data-testid="sms-reply-panel">
      <div>
        <span className={`pill ${meta.pill}`} title={meta.help}>{meta.label}</span>
        {reply.campaignName ? <span className="muted"> · campaign {reply.campaignName}</span> : null}
        {reply.templateKey ? <span className="muted"> · answered {reply.templateKey}</span> : null}
      </div>
      <div className="muted">{meta.help}</div>
      {reply.classification === 'YES_KIT' && (
        <>
          {ship ? (
            <div>
              <div>
                <span className="muted">{reply.shipTo ? 'Ship to (roster): ' : 'Ship to (matched member): '}</span>
                <strong>{[ship.line1, ship.line2].filter(Boolean).join(' ')}, {ship.city}, {ship.state} {ship.zip}</strong>
              </div>
              {(ship.mailOutcome || ship.uspsResult) && (
                <div>
                  <span className="muted">USPS says: </span>
                  {[ship.mailOutcome, ship.uspsResult].filter(Boolean).join(' · ')}
                  {ship.usps?.line1 ? <span className="muted"> · USPS address {ship.usps.line1}{ship.usps.line2 ? ` ${ship.usps.line2}` : ''}, {ship.usps.city} {ship.usps.state} {ship.usps.zip}{ship.usps.zip4 ? `-${ship.usps.zip4}` : ''}</span> : null}
                </div>
              )}
            </div>
          ) : (
            <div className="muted">No roster address was found for this reply at intake; check the member before anything ships.</div>
          )}
          {!replyButtons && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-sm"
                disabled={disabled || shipPath === 'undecided' || !ship}
                title={wording.note}
                onClick={() => onConfirm(shipPath)}
              >
                {wording.button}
              </button>
              <span className="muted">{wording.note}</span>
            </div>
          )}
        </>
      )}
      {replyButtons && shown.length > 0 && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }} data-testid="reply-press">
          {shown.map((op) => (
            <button
              key={op}
              type="button"
              className={op === 'stop' ? 'btn btn-sm btn-ghost' : 'btn btn-sm btn-primary'}
              disabled={pressDisabled}
              title={replyPressHelp(op)}
              onClick={() => onPress?.(op)}
            >
              {own ? REPLY_OP_WORDING[op].button : REPLY_OP_WORDING[op].move}
            </button>
          ))}
        </div>
      )}
      {replyButtons && shown.length > 0 && pressNote ? (
        <div className="muted" role="note" data-testid="reply-press-note">{pressNote}</div>
      ) : null}
    </div>
  );
}

function HaltPhysicians({ halt }: { halt: EventReferralHalt }) {
  const who = (s: { pcpNpi: string | null; pcpName: string; faxNumber: string }) => {
    const unresolved = !s.pcpNpi && s.pcpName === PRACTICE_NOT_RESOLVED;
    return (
      <>
        {unresolved ? (
          <span className="pill pill-warning" title="Shared fax number; no NPI on the historical row">
            practice not resolved
          </span>
        ) : (
          <strong>{s.pcpName || '(no practice named)'}</strong>
        )}
        {s.pcpNpi ? <span className="muted"> · NPI {s.pcpNpi}</span> : null}
        {s.faxNumber ? <span className="muted"> · fax {s.faxNumber}</span> : null}
      </>
    );
  };
  return (
    <div className="micro" style={{ marginTop: 4, display: 'grid', gap: 2 }} data-testid="halt-physicians">
      <div>
        <span className="muted">Previously sent to </span>
        {who(halt.previousSend)}
        {halt.previousSend.sentTs ? <span className="muted"> · {fmtTs(halt.previousSend.sentTs)}</span> : null}
      </div>
      <div>
        <span className="muted">Attempted send to </span>
        {who(halt.attemptedSend)}
      </div>
      {halt.resultEvent.kitBarcode ? (
        <div className="muted">
          Result kit {halt.resultEvent.kitBarcode}
          {halt.resultEvent.resultedTs ? ` · resulted ${fmtTs(halt.resultEvent.resultedTs)}` : ''}
        </div>
      ) : null}
      {halt.faxJobId ? (
        <div className="muted">
          Fax job {halt.faxJobId}
          {halt.attempt ? ` · attempt ${halt.attempt}` : ''}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The halted fax job's key for a pcp-fax-resend recorded on a fax-lane halt (cdx-cc-referral/2):
 * the resolution names the exact Fax Station job and attempt it overrides, so the Portal's S5 closes
 * THAT halt. Empty on any event without a halt, so every other pcp-fax-resend is unchanged.
 */
function haltKey(ev: EventRecord): { haltFaxJobId: string; haltAttempt: number } | Record<string, never> {
  const h = ev.referral?.halt;
  return h?.faxJobId ? { haltFaxJobId: h.faxJobId, haltAttempt: h.attempt } : {};
}
